import { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useToast } from '../../../components/Toast'
import { fetchDefaultFiles, addLocalFile, removeLocalFile, removeLocalFiles } from '../../../slice/defaultFilesSlice'
import { selectSelectedTask } from '../../../slice/taskSlice'
import { fetchAgentLayout, updateShowFilesTab, updateAllowLocalFiles } from '../../../slice/agentWidgetsSlice'
import { Upload, Trash2, FileText, Loader2, AlertTriangle, Plus, MoreHorizontal, LayoutGrid, List, FolderClosed, X, Folder, ChevronLeft, Globe, ChevronDown, ExternalLink, RefreshCw, CheckCircle2, Info, CalendarClock, WifiOff } from 'lucide-react'
import axios from 'axios'
import { BASE_FILEURL, BASE_SHAREPOINT_URL } from '../../../components/constants'
import SharePointFileBrowser from './SharePointFileBrowser'
import SyncReportsTab from './SyncReportsTab'
import ImportReportModal from './ImportReportModal'
import AddSharePointSiteModal from './AddSharePointSiteModal'
import SyncScheduleModal from './SyncScheduleModal'
import ImportedFilesBrowserModal from './ImportedFilesBrowserModal'
import FailedEmbedTab from './FailedEmbedTab'
import { fetchSharePointConnection, fetchConfiguredSites, browseSharePointFiles, clearBrowseItems, fetchAutoSyncFolders, addAutoSyncFolder, removeAutoSyncFolder, updateAutoSyncFolder, clearAutoSyncFolders, fetchAuditLogs, syncSharePointFiles, syncAllSharePointFiles, unsyncAllSharePointFiles, setSyncAllProgress, syncAllStaticFiles, resumeStaticSync, retryFailedFilesInReport, cancelStaticSync, setStaticSyncProgress, setStaticSyncPhase, setStaticSyncSite, clearStaticSync, hideSharePointSiteForTask } from '../../../slice/sharepointSlice'
import { useThemeColor } from '../../../hooks/useThemeColor'
import { useTranslation } from '../../../i18n/LanguageContext'
import { authHeader } from '../../../utils/authHeaders';

const DefaultFiles = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { addToast, ToastContainer } = useToast()
  const { aispaceColor } = useThemeColor()
  const fileInputRef = useRef(null)

  const { data } = useSelector((state) => state.admin)
  const { organizations } = useSelector((state) => state.organizations)
  const selectedTask = useSelector(selectSelectedTask)
  const { files: defaultFiles, loading: defaultFilesLoading } = useSelector((state) => state.defaultFiles)
  const { showFilesTabByTask, allowLocalFilesByTask, layoutLoadingByTask, layoutByTask } = useSelector((state) => state.agentWidgets)
  const taskIdStr = selectedTask?.id?.toString()
  const isLayoutLoading = taskIdStr ? layoutLoadingByTask[taskIdStr] === true : false
  const showFilesTab = taskIdStr ? (showFilesTabByTask[taskIdStr] === true) : false
  const allowLocalFiles = taskIdStr ? (allowLocalFilesByTask[taskIdStr] === true) : false

  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [currentFileIndex, setCurrentFileIndex] = useState(0)
  const [totalFiles, setTotalFiles] = useState(0)
  const [currentFileName, setCurrentFileName] = useState('')
  const [selectedOrganization, setSelectedOrganization] = useState('')
  const [showFolderModal, setShowFolderModal] = useState(false)
  const [deletingFileId, setDeletingFileId] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showSpBrowser, setShowSpBrowser] = useState(false)
  const [hasSpConnection, setHasSpConnection] = useState(false)
  const [activeTab, setActiveTab] = useState('all')
  const [allFilesSearch, setAllFilesSearch] = useState('')
  const [allFilesPage, setAllFilesPage] = useState(1)
  const ALL_FILES_PAGE_SIZE = 10
  // Filter modal state
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const [filterFileType, setFilterFileType] = useState(null) // 'local' | 'sharepoint' | null
  const [filterSortBy, setFilterSortBy] = useState('newest') // 'newest' | 'oldest'
  const [filterSortOpen, setFilterSortOpen] = useState(false)
  // Draft (inside modal) — only committed on Apply
  const [draftFileType, setDraftFileType] = useState(null)
  const [draftSortBy, setDraftSortBy] = useState('newest')
  const [viewMode, setViewMode] = useState('list')
  const [activeMenu, setActiveMenu] = useState(null)
  const [selectedFolderName, setSelectedFolderName] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, type: null, file: null, folder: null, spItem: null })

  // Folder creation state
  const [folderName, setFolderName] = useState('')
  const [selectedFiles, setSelectedFiles] = useState([])
  const [isDragging, setIsDragging] = useState(false)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  const isSuperAdmin = data?.user_role === "SUPER_ADMIN"
  const isAdmin = data?.user_role === "ADMIN"
  const userOrganizationId = data?.organization_id

  // Use task's org if available, otherwise fall back to the logged-in user's org
  // (mirrors HomeScreen project-create flow which works with user's org credentials)
  const taskOrganizationId = selectedTask?.organization_id || userOrganizationId
  const { configuredSites, configuredSitesLoading, browseItems, browseLoading, syncAllLoading, syncAllResults, syncAllError, syncAllProgress, unsyncAllLoading, unsyncAllError, staticSyncLoading, staticSyncResults, staticSyncError, staticSyncProgress, staticSyncPhase, staticSyncSite, staticSyncJobId, staticSyncCancelling, staticSyncConnectionLost } = useSelector((state) => state.sharepoint)
  const [addSiteModalOpen, setAddSiteModalOpen] = useState(false)
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false)
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false)
  const [cancellingAction, setCancellingAction] = useState(null) // null | 'stop' | 'discard' — which button is in flight
  const [cancelRequestedForJobId, setCancelRequestedForJobId] = useState(null) // job_id a cancel was already sent for — keeps "Cancel" disabled until a NEW job starts (staticSyncJobId changes), so it can't be clicked repeatedly while the backend is still winding down
  const [editSourcesSite, setEditSourcesSite] = useState(null)
  const [importedFilesBrowserSite, setImportedFilesBrowserSite] = useState(null)
  const [openedSite, setOpenedSite] = useState(null) // site object when user clicks "Site öffnen"
  const [spPathStack, setSpPathStack] = useState(['/']) // breadcrumb stack inside a site
  const [siteCounts, setSiteCounts] = useState({}) // { [siteId]: { files, folders, subfolders, loading } }
  const [siteStats, setSiteStats] = useState({}) // { [siteId]: { has_report, files_imported, skipped, failed, last_import_at, report_id, sources, loading } }
  const [siteCardMenu, setSiteCardMenu] = useState(null) // site id for open "..." menu on a site card
  const [importReportModal, setImportReportModal] = useState(null) // { reportId, sources } for "View import report"
  const [deletionReportModal, setDeletionReportModal] = useState(null) // { reportId, siteName } for "View deletion report"
  const [removingSiteId, setRemovingSiteId] = useState(null) // "Remove site from agent" in-flight
  const [spSitesFetched, setSpSitesFetched] = useState(false)
  // File selection for import
  const [selectedSpFiles, setSelectedSpFiles] = useState({}) // { [id]: item }
  const [spImporting, setSpImporting] = useState(false)
  // TEMPORARY (2026-06-23): content/rename monitoring is frozen for single-file
  // imports (see comment in routers/sharepoint.py sync_files()). This toggle only
  // controls the opt-in delete-only monitoring for those imports.
  const [importWatchDeletes, setImportWatchDeletes] = useState(false)

  const toggleSpFileSelect = (item) => {
    if (item.type === 'folder') return
    setSelectedSpFiles(prev => {
      const next = { ...prev }
      if (next[item.id]) delete next[item.id]
      else next[item.id] = item
      return next
    })
  }

  const toggleSelectAll = () => {
    const files = (browseItems || []).filter(i => i.type !== 'folder')
    const allSelected = files.length > 0 && files.every(f => selectedSpFiles[f.id])
    if (allSelected) {
      setSelectedSpFiles({})
    } else {
      const next = {}
      files.forEach(f => { next[f.id] = f })
      setSelectedSpFiles(next)
    }
  }

  const handleImportSelected = async () => {
    if (!openedSite || !taskOrganizationId || !selectedTask?.id || spImporting) return
    const items = Object.values(selectedSpFiles)
    if (items.length === 0) return
    setSpImporting(true)
    try {
      // One-shot file-level sync. Does NOT register an auto-sync folder rule
      // (folder + folder+subfolder auto-sync is intentionally disabled).
      const syncItems = items
        .filter(it => it.type !== 'folder')
        .map(it => ({
          item_id: it.id,
          file_name: it.name,
          file_path: getFolderPathForItem(it.name),
          etag: it.eTag || null,
          last_modified: it.lastModifiedDateTime || null,
        }))
      if (syncItems.length === 0) {
        addToast(t('agentSettings.filesImportError'), 'error')
        return
      }
      await dispatch(syncSharePointFiles({
        org_id: taskOrganizationId,
        task_id: selectedTask.id.toString(),
        site_id: openedSite.id,
        items: syncItems,
        watch_deletes: importWatchDeletes,
      })).unwrap()
      dispatch(fetchDefaultFiles({ organizationId: taskOrganizationId || null, taskId: selectedTask.id.toString() }))
      dispatch(fetchAuditLogs({ taskId: selectedTask.id.toString(), limit: 100, offset: 0 }))
      setSelectedSpFiles({})
      addToast(t('agentSettings.filesImportSuccess', { count: syncItems.length }), 'success')
    } catch (err) {
      addToast(t('agentSettings.filesImportError'), 'error')
    } finally {
      setSpImporting(false)
    }
  }

  // sharepointView removed — SharePoint tab always shows connected sites overview
  const [spSubTab, setSpSubTab] = useState('overview') // 'overview' | 'protocol'
  const [flatMenuPos, setFlatMenuPos] = useState(null) // position for SharePoint flat list dropdown

  const openFlatMenu = (e, itemId) => {
    e.stopPropagation()
    if (activeMenu === itemId) {
      setActiveMenu(null); setFlatMenuPos(null)
      return
    }
    const rect = e.currentTarget.getBoundingClientRect()
    const menuWidth = 160
    const menuHeight = 60
    const spaceBelow = window.innerHeight - rect.bottom
    const left = Math.max(8, Math.min(window.innerWidth - menuWidth - 8, rect.right - menuWidth))
    const pos = spaceBelow >= menuHeight + 8
      ? { top: rect.bottom + 4, left }
      : { bottom: (window.innerHeight - rect.top) + 4, left }
    setFlatMenuPos(pos)
    setActiveMenu(itemId)
  }

  // Fetch agent layout to get showFilesTab flag
  useEffect(() => {
    const tid = selectedTask?.id?.toString()
    if (tid && !layoutLoadingByTask[tid] && !layoutByTask[tid]) {
      dispatch(fetchAgentLayout(tid))
    }
  }, [selectedTask?.id, dispatch, layoutLoadingByTask, layoutByTask])

  // Fetch audit logs when Sync Protocol tab is opened
  useEffect(() => {
    if (activeTab === 'sharepoint' && spSubTab === 'protocol' && selectedTask?.id) {
      dispatch(fetchAuditLogs({ taskId: selectedTask.id.toString(), limit: 100, offset: 0 }))
    }
  }, [activeTab, spSubTab, selectedTask?.id, dispatch])
  const { autoSyncFolders, addAutoSyncLoading, removeAutoSyncLoading } = useSelector((state) => state.sharepoint)

  // Helper: find auto-sync folder entry by path
  const getAutoSyncFolder = (folderPath) => (autoSyncFolders || []).find(f => f.folder_path === folderPath)
  const isAutoSyncEnabled = (folderPath) => !!getAutoSyncFolder(folderPath)
  // True if an ancestor folder is synced with include_subfolders=true (inherited sync)
  const hasInheritedSync = (itemPath) => {
    if (!itemPath) return false
    return (autoSyncFolders || []).some(f =>
      f.include_subfolders && f.folder_path && itemPath.startsWith(`${f.folder_path}/`)
    )
  }
  // Effective "synced" state: directly synced OR inherited from ancestor-with-subfolders
  const isItemSynced = (itemPath) => isAutoSyncEnabled(itemPath) || hasInheritedSync(itemPath)
  // True if any DESCENDANT (strictly inside) has an individual sync entry
  const hasSyncedDescendant = (folderPath) => {
    if (!folderPath) return false
    return (autoSyncFolders || []).some(f => f.folder_path?.startsWith(`${folderPath}/`))
  }
  const getFolderPathForItem = (itemName) => {
    const currentPath = spPathStack[spPathStack.length - 1]
    return currentPath === '/' ? `/${itemName}` : `${currentPath}/${itemName}`
  }

  const handleEnableAutoSync = async (item, includeSubfolders) => {
    if (!openedSite || !taskOrganizationId || !selectedTask?.id) return
    const folderPath = getFolderPathForItem(item.name)
    const existing = getAutoSyncFolder(folderPath)
    setSpItemActionLoading(item.id)
    try {
      if (existing) {
        await dispatch(updateAutoSyncFolder({ folderId: existing.id, includeSubfolders })).unwrap()
      } else {
        await dispatch(addAutoSyncFolder({
          siteId: openedSite.id,
          taskId: selectedTask.id.toString(),
          folderPath,
          folderItemId: item.id,
          includeSubfolders,
          importExisting: true,
        })).unwrap()
      }
      await dispatch(fetchAutoSyncFolders({ orgId: taskOrganizationId, siteId: openedSite.id, taskId: selectedTask.id.toString() }))
      // Refresh imported files + audit log so newly-synced content appears immediately
      dispatch(fetchDefaultFiles({ organizationId: taskOrganizationId || null, taskId: selectedTask.id.toString() }))
      dispatch(fetchAuditLogs({ taskId: selectedTask.id.toString(), limit: 100, offset: 0 }))
      addToast(includeSubfolders ? t('agentSettings.filesAutoSyncSubfoldersEnabled') : t('agentSettings.filesAutoSyncFolderOnlyEnabled'), 'success')
    } catch (err) {
      addToast(t('agentSettings.filesAutoSyncEnableError'), 'error')
    } finally {
      setSpItemActionLoading(null)
    }
  }

  const handleDisableAutoSync = async (item) => {
    if (!openedSite) return
    const folderPath = getFolderPathForItem(item.name)
    const existing = getAutoSyncFolder(folderPath)
    if (!existing) {
      if (hasInheritedSync(folderPath)) {
        addToast(t('agentSettings.filesInheritedSyncNotice'), 'error')
      }
      return
    }
    setSpItemActionLoading(item.id)
    try {
      await dispatch(removeAutoSyncFolder({ folderId: existing.id, deleteFiles: true })).unwrap()
      await dispatch(fetchAutoSyncFolders({ orgId: taskOrganizationId, siteId: openedSite.id, taskId: selectedTask.id.toString() }))
      // Refresh imported files + audit log (deletions reflect immediately)
      dispatch(fetchDefaultFiles({ organizationId: taskOrganizationId || null, taskId: selectedTask.id.toString() }))
      dispatch(fetchAuditLogs({ taskId: selectedTask.id.toString(), limit: 100, offset: 0 }))
      addToast(t('agentSettings.filesAutoSyncDisabled'), 'success')
    } catch (err) {
      addToast(t('agentSettings.filesAutoSyncDisableError'), 'error')
    } finally {
      setSpItemActionLoading(null)
    }
  }
  const [spRowMenuOpen, setSpRowMenuOpen] = useState(null) // row id whose menu is open
  const [spRowMenuPos, setSpRowMenuPos] = useState(null) // { top, left } for fixed dropdown
  const [spItemActionLoading, setSpItemActionLoading] = useState(null) // item id currently toggling auto-sync
  const [syncAllSite, setSyncAllSite] = useState(null) // site being sync-all'd (old monitor flow)
  const [showSyncAllResults, setShowSyncAllResults] = useState(false)
  const [unsyncAllSite, setUnsyncAllSite] = useState(null) // site pending unsync-all confirmation
  const [removeSiteConfirm, setRemoveSiteConfirm] = useState(null) // site pending "remove from agent" confirmation
  const [removeSiteConfirmError, setRemoveSiteConfirmError] = useState(null)

  // ── Static Sync All dialog + minimizable progress ──
  const [showSyncDialog, setShowSyncDialog] = useState(false)
  const [syncDialogSite, setSyncDialogSite] = useState(null)
  // Dialog options
  const [syncFileTypes, setSyncFileTypes] = useState({ pdf: true, docx: true, doc: true, xlsx: true, xls: true, pptx: true, ppt: true })
  const [syncMaxPages, setSyncMaxPages] = useState(0)

  const openSpRowMenu = (e, itemId) => {
    e.stopPropagation()
    if (spRowMenuOpen === itemId) {
      setSpRowMenuOpen(null); setSpRowMenuPos(null)
      return
    }
    const rect = e.currentTarget.getBoundingClientRect()
    const menuWidth = 240
    const menuHeight = 160 // approx height of dropdown with 3 options
    const spaceBelow = window.innerHeight - rect.bottom
    const left = Math.max(8, Math.min(window.innerWidth - menuWidth - 8, rect.right - menuWidth))
    // If enough space below → anchor top to button bottom
    // If not → anchor BOTTOM of menu just above the button (so menu sits directly above button)
    const pos = spaceBelow >= menuHeight + 8
      ? { top: rect.bottom + 4, left }
      : { bottom: (window.innerHeight - rect.top) + 4, left }
    setSpRowMenuPos(pos)
    setSpRowMenuOpen(itemId)
  }

  const handleSpDelete = async (item) => {
    try {
      await axios.delete(`${BASE_SHAREPOINT_URL}/browse/${taskOrganizationId}/item`, {
        params: { site_id: openedSite?.id, item_id: item.id },
        headers: { ...authHeader() }
      })
      if (openedSite) {
        dispatch(browseSharePointFiles({
          orgId: taskOrganizationId,
          siteId: openedSite.id,
          path: spPathStack[spPathStack.length - 1],
          taskId: selectedTask?.id?.toString(),
        }))
      }
      addToast(t('agentSettings.filesSpItemDeleted'), 'success')
    } catch (err) {
      addToast(t('agentSettings.filesSpItemDeleteError'), 'error')
    }
  }

  // "Remove site from agent" (3-dot menu, confirmed via removeSiteConfirm
  // dialog) — unassigns the site AND deletes everything imported from it for
  // this task (documents, embeddings, mappings; backend also clears the
  // source selection). Org connection and other agents are untouched.
  const handleRemoveSiteFromAgent = async () => {
    const site = removeSiteConfirm
    if (!site) return
    setRemovingSiteId(site.id)
    setRemoveSiteConfirmError(null)
    try {
      const result = await dispatch(hideSharePointSiteForTask({ orgId: taskOrganizationId, siteId: site.id, taskId: taskIdStr })).unwrap()
      dispatch(fetchConfiguredSites({ orgId: taskOrganizationId, taskId: taskIdStr }))
      addToast(t('agentSettings.filesSiteRemovedFromAgent'), 'success')
      setRemoveSiteConfirm(null)
      if (result?.report_id) setDeletionReportModal({ reportId: result.report_id, siteName: site.site_name, sources: siteStats[site.id]?.sources })
    } catch (err) {
      const code = err?.code
      if (code === 'import_already_running') {
        setRemoveSiteConfirmError(t('agentSettings.filesRetryImportRunning'))
      } else {
        setRemoveSiteConfirmError(typeof err === 'string' ? err : t('agentSettings.filesRemoveSiteError'))
      }
    } finally {
      setRemovingSiteId(null)
    }
  }

  const handleSyncAll = (site) => {
    setSyncDialogSite(site)
    const lastTypes = siteStats[site.id]?.last_file_types
    const hasFilesNow = (siteStats[site.id]?.files_imported || 0) > 0
    // pptx/ppt restore from the actual last-used selection, exactly like
    // every other file type — if it was unchecked on purpose last time,
    // that choice is respected, not silently overridden. But if the site
    // currently has 0 imported files, old report history is stale/irrelevant
    // (e.g. everything was deleted since) — treat it as a fresh import.
    if (hasFilesNow && lastTypes && lastTypes.length > 0) {
      const typesSet = new Set(lastTypes.map(t => t.toLowerCase()))
      setSyncFileTypes({
        pdf: typesSet.has('pdf'),
        docx: typesSet.has('docx'),
        doc: typesSet.has('doc'),
        xlsx: typesSet.has('xlsx'),
        xls: typesSet.has('xls'),
        pptx: typesSet.has('pptx'),
        ppt: typesSet.has('ppt'),
      })
    } else {
      setSyncFileTypes({ pdf: true, docx: true, doc: true, xlsx: true, xls: true, pptx: true, ppt: true })
    }
    setSyncMaxPages(siteStats[site.id]?.last_max_pages ?? 0)
    setShowSyncDialog(true)
  }

  // Called when user confirms in the dialog
  const handleSyncAllConfirm = async () => {
    if (!syncDialogSite) return
    setShowSyncDialog(false)
    const site = syncDialogSite
    dispatch(setStaticSyncSite(site))
    dispatch(setStaticSyncProgress(null))

    const enabledTypes = Object.entries(syncFileTypes)
      .filter(([, v]) => v)
      .map(([k]) => k)

    try {
      const result = await dispatch(syncAllStaticFiles({
        org_id: taskOrganizationId,
        task_id: selectedTask.id.toString(),
        site_id: site.id,
        file_types: enabledTypes,
        max_pages: syncMaxPages,
        force_reimport: true,
        watch_deletes: false,
      })).unwrap()
      dispatch(fetchDefaultFiles({ organizationId: taskOrganizationId, taskId: selectedTask.id.toString() }))
    } catch (err) {
      // phase is set to 'error' by the rejected extraReducer
    }
  }

  // Retries only the failed files of an existing report, updating that same
  // report in place — distinct from handleSyncAllConfirm, which force-
  // reimports the whole site into a brand-new report.
  const handleRetryFailed = async (site, reportId) => {
    if (!reportId) return
    dispatch(setStaticSyncSite(site))
    dispatch(setStaticSyncProgress(null))
    try {
      await dispatch(retryFailedFilesInReport({ report_id: reportId })).unwrap()
      dispatch(fetchDefaultFiles({ organizationId: taskOrganizationId, taskId: selectedTask.id.toString() }))
      refreshSiteStats(site)
    } catch (err) {
      const code = err?.code
      if (code === 'report_not_current') {
        addToast(t('agentSettings.filesRetryReportNotCurrent'), 'error')
      } else if (code === 'import_already_running') {
        addToast(t('agentSettings.filesRetryImportRunning'), 'error')
      } else {
        addToast(t('agentSettings.filesRetryFailedError'), 'error')
      }
    }
  }

  const handleUnsyncAll = async () => {
    if (!unsyncAllSite) return
    const site = unsyncAllSite
    try {
      const result = await dispatch(unsyncAllSharePointFiles({
        org_id: taskOrganizationId,
        task_id: selectedTask.id.toString(),
        site_id: site.id,
      })).unwrap()
      dispatch(fetchDefaultFiles({ organizationId: taskOrganizationId, taskId: selectedTask.id.toString() }))
      refreshSiteStats(site)
      setUnsyncAllSite(null)
      if (result?.report_id) setDeletionReportModal({ reportId: result.report_id, siteName: site.site_name, sources: siteStats[site.id]?.sources })
    } catch (err) {
      // Dialog stays open so the error below is visible; unsyncAllError may
      // be a {code: '...'} object (e.g. import already running) rather than
      // a plain string.
    }
  }

  const closeOpenedSite = () => {
    setOpenedSite(null)
    setSpPathStack(['/'])
    setSelectedSpFiles({})
    dispatch(clearBrowseItems())
    dispatch(clearAutoSyncFolders())
  }

  const navigateIntoFolder = (folderName) => {
    if (!openedSite) return
    const currentPath = spPathStack[spPathStack.length - 1]
    const newPath = currentPath === '/' ? `/${folderName}` : `${currentPath}/${folderName}`
    const newStack = [...spPathStack, newPath]
    setSpPathStack(newStack)
    setSelectedSpFiles({})
    dispatch(browseSharePointFiles({ orgId: taskOrganizationId, siteId: openedSite.id, path: newPath, taskId: selectedTask?.id?.toString() }))
  }

  const navigateBreadcrumb = (index) => {
    if (!openedSite) return
    const newStack = spPathStack.slice(0, index + 1)
    setSpPathStack(newStack)
    setSelectedSpFiles({})
    dispatch(browseSharePointFiles({ orgId: taskOrganizationId, siteId: openedSite.id, path: newStack[newStack.length - 1], taskId: selectedTask?.id?.toString() }))
  }


  useEffect(() => {
    if (taskOrganizationId) {
      dispatch(fetchSharePointConnection(taskOrganizationId)).unwrap()
        .then(result => setHasSpConnection(result?.connection_status === 'connected' || result?.connection_status === 'error'))
        .catch(() => setHasSpConnection(false))
      setSpSitesFetched(false)
      dispatch(fetchConfiguredSites({ orgId: taskOrganizationId, taskId: taskIdStr })).finally(() => {
        setSpSitesFetched(true)
      })
    } else {
      setHasSpConnection(false)
      setSpSitesFetched(true)
    }
  }, [dispatch, taskOrganizationId, taskIdStr])

  // Fetch file/folder/subfolder counts for each connected site (root-level browse)
  const siteIdsKey = (configuredSites || []).map(s => s.id).join(',')
  useEffect(() => {
    if (activeTab !== 'sharepoint' || !taskOrganizationId || !(configuredSites || []).length) return
    let cancelled = false
    ;(configuredSites || []).forEach(async (site) => {
      // Skip if already fetched or currently loading
      if (siteCounts[site.id] && !siteCounts[site.id].loading) return
      if (siteCounts[site.id]?.loading) return
      setSiteCounts(prev => ({ ...prev, [site.id]: { loading: true, files: 0, folders: 0, subfolders: 0 } }))
      try {
        const res = await axios.get(`${BASE_SHAREPOINT_URL}/browse/${taskOrganizationId}`, {
          params: { site_id: site.id, path: '/' },
          headers: { ...authHeader() }
        })
        const items = res.data?.items || res.data || []
        const files = items.filter(i => i.type === 'file').length
        const folders = items.filter(i => i.type === 'folder').length
        const subfolders = items
          .filter(i => i.type === 'folder')
          .reduce((sum, f) => sum + (f.childCount || 0), 0)
        if (!cancelled) {
          setSiteCounts(prev => ({ ...prev, [site.id]: { loading: false, files, folders, subfolders } }))
        }
      } catch (err) {
        if (!cancelled) {
          setSiteCounts(prev => ({ ...prev, [site.id]: { loading: false, files: 0, folders: 0, subfolders: 0 } }))
        }
      }
    })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, taskOrganizationId, siteIdsKey])

  // Fetch (or force-refresh) import stats for one site — files imported/
  // skipped/failed/last import/sources, scoped to this agent (task_id),
  // unlike the org-wide siteCounts above which just reflects live SharePoint
  // folder contents. Exposed as a function (not just an effect) so a fresh
  // import can re-pull the same site's stats immediately after it finishes,
  // instead of waiting on the passive effect below (which only re-runs when
  // its dependencies change, not on demand).
  // checkForActiveJob defaults to false — "resume tracking after a page
  // reload" only makes sense on the very first stats load for a site (see
  // the mount-time effect below, the one call site that passes true).
  // Every other refreshSiteStats call happens right after some action WE
  // triggered (an import/retry/delete just finished) — checking again there
  // risks picking up a stale/abandoned 'running' row for this same site
  // (e.g. left behind by an earlier crashed/interrupted run, still inside
  // the server's 5-minute freshness window) and re-displaying it as
  // "Import running" right after a legitimate completion/discard, making
  // the UI look stuck even though the backend job is already done.
  // job_ids we've already adopted-and-finished tracking (started by us or
  // picked up mid-run), so a periodic scheduled-job check below never
  // re-adopts the same job's stale 'running' row after it's done — see the
  // "picks up a stale/abandoned 'running' row" note above.
  const seenActiveJobIdsRef = useRef(new Set())

  const refreshSiteStats = async (site, checkForActiveJob = false) => {
    if (!taskOrganizationId || !taskIdStr || !site?.id) return
    setSiteStats(prev => ({ ...prev, [site.id]: { ...(prev[site.id] || {}), loading: true } }))
    try {
      const res = await axios.get(`${BASE_SHAREPOINT_URL}/site-stats/${taskOrganizationId}/${taskIdStr}/${site.id}`, {
        headers: { ...authHeader() }
      })
      setSiteStats(prev => ({ ...prev, [site.id]: { ...res.data, loading: false } }))

      // Resume tracking a still-running import after a page reload
      if (checkForActiveJob && res.data.active_job_id && !staticSyncLoading) {
        dispatch(setStaticSyncSite(site))
        dispatch(setStaticSyncProgress(null))
        dispatch(resumeStaticSync({ job_id: res.data.active_job_id })).unwrap()
          .then(() => dispatch(fetchDefaultFiles({ organizationId: taskOrganizationId, taskId: selectedTask.id.toString() })))
          .catch(() => {})
      }
    } catch (err) {
      setSiteStats(prev => ({ ...prev, [site.id]: { loading: false, has_report: false, files_imported: 0, skipped: 0, failed: 0, last_import_at: null, report_id: null, sources: [] } }))
    }
  }

  useEffect(() => {
    if (activeTab !== 'sharepoint' || !taskOrganizationId || !taskIdStr || !(configuredSites || []).length) return
    ;(configuredSites || []).forEach((site) => {
      if (siteStats[site.id]) return
      refreshSiteStats(site, true) // first load for this site — safe to resume a genuinely still-running job
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, taskOrganizationId, taskIdStr, siteIdsKey])

  // Catch a sync the scheduler started while this tab was already sitting
  // open — the mount-time effect above only looks for an already-running job
  // once, on first load, so without this a scheduled run stays invisible
  // until the page is reloaded. Only polls while nothing is being tracked
  // locally (staticSyncPhase === 'idle'), and skips any job_id already in
  // seenActiveJobIdsRef so a job this tab already finished tracking can't be
  // re-adopted from a stale row still inside the server's cleanup window.
  useEffect(() => {
    if (staticSyncPhase !== 'idle') return
    if (activeTab !== 'sharepoint' || !taskOrganizationId || !taskIdStr || !(configuredSites || []).length) return

    const checkForScheduledJobs = async () => {
      for (const site of configuredSites) {
        if (staticSyncPhase !== 'idle') break
        try {
          const res = await axios.get(`${BASE_SHAREPOINT_URL}/site-stats/${taskOrganizationId}/${taskIdStr}/${site.id}`, {
            headers: { ...authHeader() }
          })
          const jobId = res.data.active_job_id
          if (jobId && !seenActiveJobIdsRef.current.has(jobId)) {
            seenActiveJobIdsRef.current.add(jobId)
            setSiteStats(prev => ({ ...prev, [site.id]: { ...res.data, loading: false } }))
            dispatch(setStaticSyncSite(site))
            dispatch(setStaticSyncProgress(null))
            dispatch(resumeStaticSync({ job_id: jobId })).unwrap()
              .then(() => dispatch(fetchDefaultFiles({ organizationId: taskOrganizationId, taskId: selectedTask.id.toString() })))
              .catch(() => {})
            break
          }
        } catch (err) {
          // Ignore — next tick retries.
        }
      }
    }

    const intervalId = setInterval(checkForScheduledJobs, 20000)
    return () => clearInterval(intervalId)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, taskOrganizationId, taskIdStr, siteIdsKey, staticSyncPhase])

  // Import now runs inline on the site card (no progress modal), so once the
  // background job finishes we just refresh that card's stats, toast the
  // outcome, and reset back to idle — "View import report" covers the detail
  // the old results modal used to show.
  useEffect(() => {
    if (staticSyncPhase === 'done' && staticSyncSite) {
      if (staticSyncJobId) seenActiveJobIdsRef.current.add(staticSyncJobId)
      refreshSiteStats(staticSyncSite)
      addToast(t('agentSettings.filesImportCompleted', { site: staticSyncSite.site_name }), 'success')
      dispatch(clearStaticSync())
    } else if (staticSyncPhase === 'error' && staticSyncSite) {
      // Cleared, not added (the opposite of 'done'): an error can be a job that
      // is still running, and the recovery effect skips anything already seen.
      if (staticSyncJobId) seenActiveJobIdsRef.current.delete(staticSyncJobId)
      const errMessage = typeof staticSyncError === 'string'
        ? staticSyncError
        : staticSyncError?.code === 'import_already_running'
          ? t('agentSettings.filesRetryImportRunning')
          : t('agentSettings.filesImportFailed')
      addToast(errMessage, 'error')
      dispatch(clearStaticSync())
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staticSyncPhase])

  const formatLastImportDateTime = (iso) => {
    if (!iso) return null
    const d = new Date(iso)
    return `${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}, ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
  }

  const formatLastSync = (site) => {
    if (!site?.last_sync_at) return t('agentSettings.filesLastSyncNever')
    const diff = Date.now() - new Date(site.last_sync_at).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return t('agentSettings.filesLastSyncJustNow')
    if (mins < 60) return t('agentSettings.filesLastSyncMinutes', { mins })
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return t('agentSettings.filesLastSyncHours', { hrs })
    const days = Math.floor(hrs / 24)
    return t('agentSettings.filesLastSyncDays', { days })
  }

  // Fetch default files when task changes
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[SP Debug] fetchDefaultFiles effect fired', {
      selectedTask_id: selectedTask?.id,
      taskOrganizationId,
      willFetch: !!selectedTask?.id,
    })
    if (selectedTask?.id) {
      dispatch(fetchDefaultFiles({
        organizationId: taskOrganizationId || null,
        taskId: selectedTask.id.toString()
      })).then((action) => {
        // eslint-disable-next-line no-console
        console.log('[SP Debug] fetchDefaultFiles result', {
          status: action.meta?.requestStatus,
          payloadLength: Array.isArray(action.payload) ? action.payload.length : 'not-array',
          firstFile: Array.isArray(action.payload) ? action.payload[0] : null,
          allUploadedBy: Array.isArray(action.payload) ? action.payload.map(f => f.uploaded_by) : null,
          allTaskIds: Array.isArray(action.payload) ? action.payload.map(f => f.task_id) : null,
        })
      })
    }
  }, [dispatch, selectedTask?.id, taskOrganizationId])

  // A file counts as SharePoint-origin if any of these signals match:
  //  - source_type === 'sharepoint'
  //  - uploaded_by starts with 'sharepoint' (e.g. 'sharepoint-auto-sync', 'sharepoint-sync')
  //  - has a sharepoint_mapping_id / sharepoint_etag / sharepoint_site_url
  const isSharePointFile = (file) => (
    file.source_type === 'sharepoint' ||
    (typeof file.uploaded_by === 'string' && file.uploaded_by.toLowerCase().startsWith('sharepoint')) ||
    !!file.sharepoint_mapping_id ||
    !!file.sharepoint_etag ||
    !!file.sharepoint_site_url
  )

  // Filter files based on active tab
  const filteredFiles = defaultFiles.filter(file => {
    if (activeTab === 'local') {
      return !isSharePointFile(file)
    } else {
      return isSharePointFile(file)
    }
  })

  // TEMPORARY DEBUG — remove after issue resolved
  if (activeTab === 'sharepoint') {
    // eslint-disable-next-line no-console
    console.log('[SP Debug]', {
      taskId: selectedTask?.id,
      taskOrganizationId,
      totalFiles: defaultFiles.length,
      spFilesCount: filteredFiles.length,
      firstFile: defaultFiles[0],
      allUploadedBy: defaultFiles.map(f => f.uploaded_by),
      allSourceTypes: defaultFiles.map(f => f.source_type),
    })
  }

  // Group files by folder
  const groupFilesByFolder = (files) => {
    const folders = {}
    files.forEach(file => {
      const folderKey = file.folder_name || t('agentSettings.filesGeneralFolder')
      if (!folders[folderKey]) {
        folders[folderKey] = {
          name: folderKey,
          files: [],
          created_at: file.created_at,
          updated_at: file.updated_at || file.created_at
        }
      }
      folders[folderKey].files.push(file)
      // Update folder dates based on files
      if (new Date(file.created_at) < new Date(folders[folderKey].created_at)) {
        folders[folderKey].created_at = file.created_at
      }
      if (new Date(file.updated_at || file.created_at) > new Date(folders[folderKey].updated_at)) {
        folders[folderKey].updated_at = file.updated_at || file.created_at
      }
    })
    return Object.values(folders)
  }

  const folders = groupFilesByFolder(filteredFiles)

  // Derive selectedFolder from live folders so newly uploaded files appear
  const selectedFolder = selectedFolderName
    ? folders.find(f => f.name === selectedFolderName) || null
    : null

  // Pagination calculations
  const displayItems = selectedFolder ? selectedFolder.files : folders
  const totalPages = Math.ceil(displayItems.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedItems = displayItems.slice(startIndex, startIndex + itemsPerPage)

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files)
    addFilesToSelection(files)
  }

  // Add files to selection with validation
  const addFilesToSelection = (files) => {
    const validFiles = files.filter(file => file.name.match(/\.(pdf|doc|docx)$/i))
    const invalidCount = files.length - validFiles.length

    if (invalidCount > 0) {
      addToast(t('agentSettings.filesInvalidFileType', { count: invalidCount }), 'warning')
    }

    const newFiles = validFiles.filter(
      newFile => !selectedFiles.some(existingFile => existingFile.name === newFile.name)
    )

    setSelectedFiles(prev => [...prev, ...newFiles])
  }

  // Remove file from selection
  const removeFileFromSelection = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
  }

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    const files = Array.from(e.dataTransfer.files)
    addFilesToSelection(files)
  }

  // Handle folder creation and file upload
  const handleCreateFolder = async () => {
    if (!folderName.trim()) {
      addToast(t('agentSettings.filesFolderNameRequired'), 'error')
      return
    }

    if (selectedFiles.length === 0) {
      addToast(t('agentSettings.filesAddAtLeastOne'), 'error')
      return
    }

    if (!selectedTask?.id) {
      addToast(t('agentSettings.filesSelectTaskRequired'), 'error')
      return
    }

    setUploading(true)
    setTotalFiles(selectedFiles.length)
    setCurrentFileIndex(0)
    setUploadProgress(0)
    setCurrentFileName('')

    let successCount = 0
    let errorCount = 0
    let limitError = null

    const totalCount = selectedFiles.length
    for (let i = 0; i < totalCount; i++) {
      const file = selectedFiles[i]
      setCurrentFileIndex(i + 1)
      setCurrentFileName(file.name)
      // Start of this file's upload — base progress reflects completed files
      const baseProgress = (i / totalCount) * 100
      setUploadProgress(baseProgress)

      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('task_id', selectedTask.id.toString())
        formData.append('uploaded_by', data.user_email)
        formData.append('folder_name', folderName.trim())

        const orgId = taskOrganizationId || userOrganizationId || selectedOrganization
        if (orgId) {
          formData.append('organization_id', orgId)
        }

        const response = await axios.post(`${BASE_FILEURL}/default/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...authHeader()
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const fileProgress = (progressEvent.loaded / progressEvent.total) * (100 / totalCount)
              setUploadProgress(baseProgress + fileProgress)
            }
          }
        })

        // Add file locally to Redux without refetching
        const now = new Date().toISOString()
        dispatch(addLocalFile({
          id: response.data.file_id,
          filename: response.data.filename,
          page_count: response.data.page_count,
          file_size: response.data.file_size,
          folder_name: response.data.folder_name,
          organization_id: response.data.organization_id,
          task_id: response.data.task_id,
          blob_path: response.data.blob_path,
          source_type: response.data.source_type || 'upload',
          is_active: 1,
          created_at: now,
          updated_at: now,
          uploaded_by: data.user_email
        }))

        successCount++
        // Ensure progress reflects this completed file
        setUploadProgress(((i + 1) / totalCount) * 100)
      } catch (error) {
        errorCount++
        console.error('Error uploading file:', file.name, error)
        const detail = error?.response?.data?.detail
        if (detail?.code === 'monthly_page_limit_exceeded') {
          limitError = detail
        }
      }
    }

    setUploadProgress(100)
    setCurrentFileName('')

    if (successCount > 0 && errorCount === 0) {
      if (selectedFolder) {
        addToast(t('agentSettings.filesAddedToFolderSuccess', { count: successCount, folderName }), 'success')
      } else {
        addToast(t('agentSettings.filesFolderCreatedSuccess', { folderName, count: successCount }), 'success')
      }
    } else if (limitError) {
      addToast(t('agentSettings.filesMonthlyPageLimitExceeded', { limit: limitError.limit, attempted: limitError.attempted }), 'error')
    } else if (successCount > 0 && errorCount > 0) {
      addToast(t('agentSettings.filesPartialSuccess', { success: successCount, errors: errorCount }), 'warning')
    } else {
      addToast(selectedFolder ? t('agentSettings.filesUploadError') : t('agentSettings.filesCreateFolderError'), 'error')
    }

    setShowFolderModal(false)
    setFolderName('')
    setSelectedFiles([])
    setSelectedOrganization('')

    setUploading(false)
    setUploadProgress(0)
    setCurrentFileIndex(0)
    setTotalFiles(0)
  }

  const handleCloseModal = () => {
    setShowFolderModal(false)
    setFolderName('')
    setSelectedFiles([])
  }

  const handleDeleteFile = async (fileId) => {
    setDeletingFileId(fileId)
    // Remember if this was the last file in the folder before removing
    const wasLastInFolder = selectedFolder && selectedFolder.files.length === 1
    try {
      await axios.delete(`${BASE_FILEURL}/default/${fileId}`, {
        headers: {
          ...authHeader()
        }
      })
      // Remove file from local state without refetching
      dispatch(removeLocalFile(fileId))
      // If this was a SharePoint file, sync rules may have been disabled server-side
      // — refresh autoSyncFolders + auditLogs so UI reflects the change immediately.
      if (activeTab === 'sharepoint' && selectedTask?.id) {
        if (openedSite?.id && taskOrganizationId) {
          dispatch(fetchAutoSyncFolders({ orgId: taskOrganizationId, siteId: openedSite.id, taskId: selectedTask.id.toString() }))
        }
        dispatch(fetchAuditLogs({ taskId: selectedTask.id.toString(), limit: 100, offset: 0 }))
      }
      addToast(t('agentSettings.filesDeleteFileSuccess'), 'success')

      // If folder becomes empty after this delete, navigate back to folder list
      if (wasLastInFolder) {
        setSelectedFolderName(null)
      }
    } catch (error) {
      addToast(t('agentSettings.filesDeleteFileError'), 'error')
      console.error('Error deleting file:', error)
    } finally {
      setDeletingFileId(null)
    }
  }

  // 'stop' keeps files already imported this run; 'discard' rolls the whole run back.
  // cancellingAction (not just a shared boolean) tracks WHICH button is in flight,
  // so clicking one doesn't show the other as loading/disabled too.
  const handleCancelStaticSync = async (action) => {
    if (!staticSyncJobId || cancellingAction) return
    setCancellingAction(action)
    try {
      await dispatch(cancelStaticSync({ job_id: staticSyncJobId, action })).unwrap()
      setCancelRequestedForJobId(staticSyncJobId)
      setCancelConfirmOpen(false)
    } catch (error) {
      // Job already finished on its own before the request landed — nothing
      // left to cancel, so just close quietly instead of showing an error.
      const message = typeof error === 'string' ? error : (error?.message || '')
      if (/not running/i.test(message)) {
        setCancelConfirmOpen(false)
      } else {
        addToast(t('agentSettings.filesImportCancelError'), 'error')
      }
    } finally {
      setCancellingAction(null)
    }
  }

  // Confirm delete from modal
  const confirmDeleteAction = async () => {
    if (isDeleting) return
    setIsDeleting(true)
    try {
      if (deleteConfirm.type === 'file' && deleteConfirm.file) {
        await handleDeleteFile(deleteConfirm.file.id)
      } else if (deleteConfirm.type === 'folder' && deleteConfirm.folder) {
        await handleDeleteFolder(deleteConfirm.folder)
      } else if (deleteConfirm.type === 'sp-item' && deleteConfirm.spItem) {
        await handleSpDelete(deleteConfirm.spItem)
      }
      setDeleteConfirm({ isOpen: false, type: null, file: null, folder: null, spItem: null })
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteFolder = async (folder) => {
    setActiveMenu(null)
    const fileIds = folder.files.map(f => f.id)
    const successfullyDeleted = []

    for (const fileId of fileIds) {
      try {
        await axios.delete(`${BASE_FILEURL}/default/${fileId}`, {
          headers: {
            ...authHeader()
          }
        })
        successfullyDeleted.push(fileId)
      } catch (error) {
        console.error('Error deleting file:', error)
      }
    }

    // Remove all deleted files from local state without refetching
    if (successfullyDeleted.length > 0) {
      dispatch(removeLocalFiles(successfullyDeleted))
    }

    addToast(t('agentSettings.filesDeleteFolderSuccess', { folderName: folder.name }), 'success')

    // If we were viewing this folder, navigate back
    if (selectedFolderName === folder.name) {
      setSelectedFolderName(null)
    }
  }

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return t('agentSettings.filesRelToday')
    if (diffDays === 1) return t('agentSettings.filesRelYesterday')
    if (diffDays < 7) return t('agentSettings.filesRelDaysAgo', { days: diffDays })
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7)
      return weeks === 1 ? t('agentSettings.filesRelWeekAgo') : t('agentSettings.filesRelWeeksAgo', { weeks })
    }
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30)
      return months === 1 ? t('agentSettings.filesRelMonthAgo') : t('agentSettings.filesRelMonthsAgo', { months })
    }
    const years = Math.floor(diffDays / 365)
    return years === 1 ? t('agentSettings.filesRelYearAgo') : t('agentSettings.filesRelYearsAgo', { years })
  }

  const handleFolderClick = (folder) => {
    setSelectedFolderName(folder.name)
    setCurrentPage(1)
  }

  const handleBackToFolders = () => {
    setSelectedFolderName(null)
    setCurrentPage(1)
  }

  if (!selectedTask?.id) {
    return (
      <div className="flex items-center justify-center py-8 text-[#6B7280]">
        <p>{t('agentSettings.filesSelectTaskHint')}</p>
      </div>
    )
  }

  return (
    <div onClick={() => setActiveMenu(null)}>
      <ToastContainer />

      {/* Files tab toggle */}
      <div className="flex items-center justify-between bg-white border border-[#E5E7EB] rounded-xl px-5 py-4 mb-6">
        <div className="min-w-0 pr-4">
          <h2 className="text-[15px] font-semibold text-[#212121]">{t('agentSettings.showFilesTabTitle')}</h2>
          <p className="text-[13px] text-[#6B7280] mt-1">{t('agentSettings.showFilesTabSubtitle')}</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={showFilesTab}
          disabled={isLayoutLoading || !taskIdStr}
          onClick={() => taskIdStr && !isLayoutLoading && dispatch(updateShowFilesTab({ taskId: taskIdStr, show: !showFilesTab }))}
          className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: showFilesTab ? aispaceColor : '#E5E7EB' }}
        >
          <span
            className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
            style={{ transform: showFilesTab ? 'translateX(22px)' : 'translateX(2px)' }}
          />
        </button>
      </div>

      {/* Allow local files toggle */}
      <div className="flex items-center justify-between bg-white border border-[#E5E7EB] rounded-xl px-5 py-4 mb-6">
        <div className="min-w-0 pr-4">
          <h2 className="text-[15px] font-semibold text-[#212121]">{t('agentSettings.allowLocalFilesTitle')}</h2>
          <p className="text-[13px] text-[#6B7280] mt-1">{t('agentSettings.allowLocalFilesSubtitle')}</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={allowLocalFiles}
          disabled={isLayoutLoading || !taskIdStr}
          onClick={() => taskIdStr && !isLayoutLoading && dispatch(updateAllowLocalFiles({ taskId: taskIdStr, allow: !allowLocalFiles }))}
          className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: allowLocalFiles ? aispaceColor : '#E5E7EB' }}
        >
          <span
            className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
            style={{ transform: allowLocalFiles ? 'translateX(22px)' : 'translateX(2px)' }}
          />
        </button>
      </div>

      {/* Tab Switcher */}
      <div className="bg-[#F5F5F5] rounded-lg p-1 flex mb-6">
        <button
          onClick={() => { setActiveTab('all'); setCurrentPage(1); setAllFilesPage(1); setSelectedFolderName(null) }}
          className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-[14px] font-medium transition-colors ${
            activeTab === 'all'
              ? 'bg-white text-[#111827] shadow-sm'
              : 'text-[#6B7280] hover:text-[#374151]'
          }`}
        >
          <FolderClosed size={18} />
          <span>{t('agentSettings.filesTabAll')}</span>
        </button>
        <button
          onClick={() => { setActiveTab('local'); setCurrentPage(1); setSelectedFolderName(null) }}
          className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-[14px] font-medium transition-colors ${
            activeTab === 'local'
              ? 'bg-white text-[#111827] shadow-sm'
              : 'text-[#6B7280] hover:text-[#374151]'
          }`}
        >
          <Upload size={18} />
          <span>{t('agentSettings.filesTabLocal')}</span>
        </button>
        <button
          onClick={() => {
            setActiveTab('sharepoint')
            setCurrentPage(1)
            setSelectedFolderName(null)
            // Refresh synced files list so newly-synced files appear immediately
            if (selectedTask?.id) {
              dispatch(fetchDefaultFiles({
                organizationId: taskOrganizationId || null,
                taskId: selectedTask.id.toString(),
              }))
            }
          }}
          className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-[14px] font-medium transition-colors ${
            activeTab === 'sharepoint'
              ? 'bg-white text-[#111827] shadow-sm'
              : 'text-[#6B7280] hover:text-[#374151]'
          }`}
        >
          <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11.5 1.5C7.4 1.5 4 4.1 3.1 7.8c-1.6.6-2.8 2-2.8 3.7 0 2.2 1.8 4 4 4h1.2V14H4.3c-1.4 0-2.5-1.1-2.5-2.5 0-1.2.9-2.3 2.1-2.5l.5-.1.1-.5C5.1 5.3 8 3 11.5 3c3.2 0 5.9 2 7 4.8l.2.5.5.1c1.5.2 2.6 1.5 2.6 3.1 0 1.7-1.4 3-3 3h-1.3v1.5h1.3c2.5 0 4.5-2 4.5-4.5 0-2.1-1.5-3.9-3.5-4.4C18.5 3.8 15.3 1.5 11.5 1.5zM12 10l-4 4h2.5v5h3v-5H16l-4-4z"/>
          </svg>
          <span>{t('agentSettings.filesTabSharePoint')}</span>
        </button>
      </div>

      {/* Sub-tabs under SharePoint: Sites / Import reports (+ legacy Sync
          Protocol / Failed tabs, kept super-admin-only) */}
      {activeTab === 'sharepoint' && (
        <div className="flex items-center gap-6 border-b border-[#E5E7EB] mb-6">
          {[
            { id: 'overview', label: t('agentSettings.filesSubTabOverview') },
            { id: 'reports', label: t('agentSettings.filesSubTabImportReports') },
            ...(isSuperAdmin ? [{ id: 'protocol', label: t('agentSettings.filesSubTabProtocol') }] : []),
            ...(isSuperAdmin ? [{ id: 'failed', label: 'Fehlgeschlagen' }] : []),
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSpSubTab(tab.id)}
              className={`pb-3 text-[14px] font-medium transition-colors relative ${
                spSubTab !== tab.id ? 'text-[#6B7280] hover:text-[#374151]' : ''
              }`}
              style={spSubTab === tab.id ? { color: aispaceColor } : {}}
            >
              {tab.label}
              {spSubTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-[2px] rounded-full" style={{ backgroundColor: aispaceColor }} />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Header Section */}
      <div className="flex items-center justify-between mb-6">
        <div>
          {selectedFolder ? (
            <div className="flex items-center gap-3">
              <button
                onClick={handleBackToFolders}
                className="p-2 hover:bg-[#F3F4F6] rounded-lg transition-colors"
              >
                <ChevronLeft size={20} className="text-[#6B7280]" />
              </button>
              <div>
                <h2 className="text-[16px] font-semibold text-[#111827]">
                  {selectedFolder.name}
                </h2>
                <p className="text-[14px] text-[#6B7280] mt-0.5">
                  {selectedFolder.files.length} {selectedFolder.files.length === 1 ? t('agentSettings.filesCountSingular') : t('agentSettings.filesCountPlural')}
                </p>
              </div>
            </div>
          ) : activeTab === 'sharepoint' && openedSite ? (
            <div className="flex items-center gap-3">
              <button
                onClick={closeOpenedSite}
                className="p-2 hover:bg-[#F3F4F6] rounded-lg transition-colors"
              >
                <ChevronLeft size={20} className="text-[#6B7280]" />
              </button>
              <div>
                <h2 className="text-[16px] font-semibold text-[#111827]">{openedSite.site_name}</h2>
                {!browseLoading && (
                  <p className="text-[14px] text-[#6B7280] mt-0.5">
                    {(browseItems || []).length} {(browseItems || []).length === 1 ? t('agentSettings.filesItemSingular') : t('agentSettings.filesItemPlural')}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <>
              <h2 className="text-[16px] font-semibold text-[#111827]">
                {activeTab === 'all'
                  ? t('agentSettings.filesTabAll')
                  : activeTab === 'local'
                    ? t('agentSettings.filesTabLocal')
                    : t('agentSettings.filesSiteOverview')}
              </h2>
              <p className="text-[14px] text-[#6B7280] mt-0.5">
                {activeTab === 'all'
                  ? t('agentSettings.filesHeaderAllSubtitle')
                  : activeTab === 'local'
                    ? t('agentSettings.filesHeaderLocalSubtitle')
                    : (configuredSites || []).length === 1
                      ? t('agentSettings.filesConnectedSitesSingular', { count: (configuredSites || []).length })
                      : t('agentSettings.filesConnectedSitesPlural', { count: (configuredSites || []).length })
                }
              </p>
            </>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle - hidden on Alle Dateien, inside folder, Sync Protocol, and SharePoint flat files list */}
          {activeTab !== 'all'
            && !selectedFolder
            && !(activeTab === 'sharepoint' && spSubTab === 'protocol')
            && !(activeTab === 'sharepoint' && spSubTab === 'reports')
            && !(activeTab === 'sharepoint' && spSubTab === 'failed')
            && (
            <div className="flex items-center border border-[#E5E7EB] rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2.5 transition-colors ${viewMode === 'grid' ? 'bg-[#F3F4F6]' : 'bg-white hover:bg-[#F9FAFB]'}`}
              >
                <LayoutGrid size={18} className={viewMode === 'grid' ? 'text-[#111827]' : 'text-[#6B7280]'} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2.5 transition-colors ${viewMode === 'list' ? 'bg-[#F3F4F6]' : 'bg-white hover:bg-[#F9FAFB]'}`}
              >
                <List size={18} className={viewMode === 'list' ? 'text-[#111827]' : 'text-[#6B7280]'} />
              </button>
            </div>
          )}

          {activeTab === 'sharepoint' && !openedSite && spSubTab === 'overview' && hasSpConnection && (isSuperAdmin || isAdmin) && (
            <button
              onClick={() => setScheduleModalOpen(true)}
              title={t('agentSettings.filesScheduleSyncButtonTitle')}
              className="flex items-center gap-2 px-3 py-2.5 border border-[#E5E7EB] rounded-lg bg-white hover:bg-[#F9FAFB] transition-colors text-[14px] font-medium text-[#374151]"
            >
              <CalendarClock size={18} className="text-[#374151]" />
              <span>{t('agentSettings.filesScheduleSyncButtonTitle')}</span>
            </button>
          )}

          {/* Add Folder / Upload File / Import Button */}
          {activeTab === 'all' ? null : activeTab === 'local' ? (
            <button
              onClick={() => {
                if (selectedFolder) {
                  setFolderName(selectedFolder.name)
                }
                setShowFolderModal(true)
              }}
              className="flex items-center gap-2 px-4 py-2.5 text-white rounded-lg text-[14px] font-medium hover:opacity-90 transition-colors"
              style={{ backgroundColor: aispaceColor }}
            >
              <Plus size={18} />
              <span>{selectedFolder ? t('agentSettings.filesUploadFile') : t('agentSettings.filesAddFolder')}</span>
            </button>
          ) : (
            !openedSite && (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => hasSpConnection && taskOrganizationId && setAddSiteModalOpen(true)}
                  disabled={!hasSpConnection || !taskOrganizationId}
                  className="flex items-center gap-2 px-5 py-2.5 text-white rounded-lg text-[14px] font-semibold hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: aispaceColor }}
                >
                  <Plus size={16} />
                  <span>{t('agentSettings.filesAddSiteButton')}</span>
                </button>
              </div>
            )
          )}
        </div>
      </div>

      {/* ==== All Files Tab (flat unified list of local + SharePoint) ==== */}
      {activeTab === 'all' ? (() => {
        // Build unified list
        const unified = (defaultFiles || [])
          .filter(f => !allFilesSearch.trim() || (f.filename || '').toLowerCase().includes(allFilesSearch.trim().toLowerCase()))
          .filter(f => {
            if (!filterFileType) return true
            const isSp = isSharePointFile(f)
            return filterFileType === 'sharepoint' ? isSp : !isSp
          })
          .sort((a, b) => {
            if (filterSortBy === 'newest') {
              return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
            }
            if (filterSortBy === 'oldest') {
              return new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime()
            }
            if (filterSortBy === 'most_progress') {
              return (b.page_count || 0) - (a.page_count || 0)
            }
            if (filterSortBy === 'least_progress') {
              return (a.page_count || 0) - (b.page_count || 0)
            }
            return 0
          })
        const totalAllPages = Math.max(1, Math.ceil(unified.length / ALL_FILES_PAGE_SIZE))
        const safePage = Math.min(allFilesPage, totalAllPages)
        const startIdx = (safePage - 1) * ALL_FILES_PAGE_SIZE
        const paged = unified.slice(startIdx, startIdx + ALL_FILES_PAGE_SIZE)

        return (
          <div className="space-y-4">
            {/* Search + Filters */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-[#9CA3AF]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
                <input
                  type="text"
                  placeholder={t('agentSettings.filesSearchPlaceholder')}
                  value={allFilesSearch}
                  onChange={(e) => { setAllFilesSearch(e.target.value); setAllFilesPage(1) }}
                  className="w-full pl-10 pr-4 py-2.5 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-0 bg-white"
                  onFocus={(e) => e.target.style.borderColor = aispaceColor}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  setDraftFileType(filterFileType)
                  setDraftSortBy(filterSortBy)
                  setFilterModalOpen(true)
                }}
                className={`flex items-center gap-2 px-4 py-2.5 text-[14px] font-medium rounded-lg transition-colors ${
                  filterFileType ? 'text-white' : 'text-[#374151] bg-white border border-[#E5E7EB] hover:bg-[#F9FAFB]'
                }`}
                style={filterFileType ? { backgroundColor: aispaceColor } : {}}
              >
                <Plus size={16} />
                <span>{filterFileType ? t('agentSettings.filesFilterLabelWithCount') : t('agentSettings.filesFilterLabel')}</span>
              </button>
            </div>

            {/* Table */}
            <div className="bg-white border border-[#EEEEEE] rounded-xl overflow-hidden">
              {defaultFilesLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 animate-spin" style={{ color: aispaceColor }} />
                </div>
              ) : unified.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-[#9CA3AF] text-[14px]">
                  <FileText size={40} className="mb-3 text-[#D1D5DB]" />
                  {allFilesSearch ? t('agentSettings.filesNoResultsFound') : t('agentSettings.filesNoFiles')}
                </div>
              ) : (
                <>
                  {/* Header */}
                  <div className="flex items-center bg-[#F9FAFB] border-b border-[#EEEEEE]">
                    <div className="flex-1 min-w-0 py-3 px-6"><span className="text-[13px] font-medium text-[#616161]">{t('agentSettings.filesColName')}</span></div>
                    <div className="w-[160px] py-3 px-4"><span className="text-[13px] font-medium text-[#616161]">{t('agentSettings.filesColType')}</span></div>
                    <div className="w-[140px] py-3 px-4"><span className="text-[13px] font-medium text-[#616161]">{t('agentSettings.filesColCreated')}</span></div>
                    <div className="w-[140px] py-3 px-4"><span className="text-[13px] font-medium text-[#616161]">{t('agentSettings.filesColModified')}</span></div>
                    <div className="w-[60px] py-3 px-4"></div>
                  </div>
                  {/* Rows */}
                  {paged.map((file, idx) => {
                    const isSp = isSharePointFile(file)
                    return (
                      <div
                        key={file.id}
                        className={`flex items-center hover:bg-[#F9FAFB] transition-colors ${idx !== paged.length - 1 ? 'border-b border-[#EEEEEE]' : ''}`}
                      >
                        <div className="flex-1 min-w-0 py-4 px-6">
                          <div className="flex items-center gap-3 min-w-0">
                            <div
                              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: `${aispaceColor}15` }}
                            >
                              <FileText size={18} style={{ color: aispaceColor }} />
                            </div>
                            <span className="text-[14px] font-medium text-[#111827] truncate" title={file.filename}>{file.filename}</span>
                          </div>
                        </div>
                        <div className="w-[160px] py-4 px-4">
                          <span className="text-[13px] text-[#616161]">{isSp ? t('agentSettings.filesTypeSharePoint') : t('agentSettings.filesTypeLocal')}</span>
                        </div>
                        <div className="w-[140px] py-4 px-4">
                          <span className="text-[13px] text-[#616161]">{formatDate(file.created_at)}</span>
                        </div>
                        <div className="w-[140px] py-4 px-4">
                          <span className="text-[13px] text-[#616161]">{formatDate(file.updated_at || file.created_at)}</span>
                        </div>
                        <div className="w-[60px] py-4 px-4 relative">
                          <button
                            onClick={(e) => openFlatMenu(e, file.id)}
                            className="p-1.5 border border-[#E5E7EB] hover:bg-[#F3F4F6] rounded-lg transition-colors"
                          >
                            <MoreHorizontal size={18} className="text-[#6B7280]" />
                          </button>
                          {activeMenu === file.id && flatMenuPos && (
                            <div
                              className="fixed bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-[60] min-w-[160px] overflow-hidden"
                              style={flatMenuPos}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={() => {
                                  setActiveMenu(null); setFlatMenuPos(null)
                                  setDeleteConfirm({ isOpen: true, type: 'file', file, folder: null, spItem: null })
                                }}
                                className="w-full px-4 py-2.5 text-left text-[14px] text-red-600 hover:bg-[#F9FAFB] flex items-center gap-2"
                              >
                                <Trash2 size={16} />
                                <span>{t('agentSettings.filesDelete')}</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                  {/* Pagination */}
                  {unified.length > ALL_FILES_PAGE_SIZE && (
                    <div className="flex items-center justify-between px-6 py-4 border-t border-[#EEEEEE]">
                      <span className="text-[14px] text-[#6B7280]">{t('agentSettings.filesPageOf', { current: safePage, total: totalAllPages })}</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setAllFilesPage(p => Math.max(1, p - 1))}
                          disabled={safePage === 1}
                          className="px-4 py-2 text-[14px] font-medium text-[#374151] border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] disabled:opacity-50 disabled:cursor-not-allowed transition-colors bg-white"
                        >{t('agentSettings.filesPrevious')}</button>
                        <button
                          onClick={() => setAllFilesPage(p => Math.min(totalAllPages, p + 1))}
                          disabled={safePage >= totalAllPages}
                          className="px-4 py-2 text-[14px] font-medium text-[#374151] border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] disabled:opacity-50 disabled:cursor-not-allowed transition-colors bg-white"
                        >{t('agentSettings.filesNext')}</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )
      })() : activeTab === 'sharepoint' && spSubTab === 'protocol' ? (
        <SyncReportsTab taskId={taskIdStr} reportType="delete" />
      ) : activeTab === 'sharepoint' && spSubTab === 'reports' ? (
        <SyncReportsTab taskId={taskIdStr} reportType="import" />
      ) : activeTab === 'sharepoint' && spSubTab === 'failed' ? (
        <FailedEmbedTab taskId={taskIdStr} />
      ) : activeTab === 'sharepoint' && !selectedFolder ? (
        openedSite ? (
          /* ==== Inside a site: breadcrumb + files/folders table ==== */
          <div>
            {/* Breadcrumb */}
            <div className="flex items-center flex-wrap gap-1 text-[14px] text-[#6B7280] mb-4">
              <button onClick={closeOpenedSite} className="hover:text-[#111827] transition-colors">{t('agentSettings.filesSiteOverview')}</button>
              <span className="mx-1">/</span>
              <button
                onClick={() => navigateBreadcrumb(0)}
                className={spPathStack.length === 1 ? 'font-semibold text-[#111827]' : 'hover:text-[#111827] transition-colors'}
              >
                {openedSite.site_name}
              </button>
              {spPathStack.slice(1).map((p, i) => {
                const label = p.split('/').filter(Boolean).pop()
                const isLast = i === spPathStack.length - 2
                return (
                  <span key={i} className="flex items-center gap-1">
                    <span className="mx-1">/</span>
                    <button
                      onClick={() => navigateBreadcrumb(i + 1)}
                      className={isLast ? 'font-semibold text-[#111827]' : 'hover:text-[#111827] transition-colors'}
                    >
                      {label}
                    </button>
                  </span>
                )
              })}
            </div>

            <div className="bg-white border border-[#EEEEEE] rounded-xl">
              {/* Section header - only after browse loaded */}
              {!browseLoading && (
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#F3F4F6]">
                  <div>
                    <h3 className="text-[16px] font-semibold text-[#111827]">{t('agentSettings.filesFilesAndFolders')}</h3>
                    {(() => {
                      const total = (browseItems || []).length
                      const visibleSynced = (browseItems || []).filter(it =>
                        isItemSynced(getFolderPathForItem(it.name)) || (it.type === 'file' && !!it.is_imported)
                      ).length
                      return (
                        <p className="text-[13px] text-[#6B7280] mt-0.5">
                          {total} {total === 1 ? t('agentSettings.filesItemSingular') : t('agentSettings.filesItemPlural')}
                          {visibleSynced > 0 && (
                            <span className="ml-2 text-green-700">· {t('agentSettings.filesSyncedSuffix', { count: visibleSynced })}</span>
                          )}
                        </p>
                      )
                    })()}
                  </div>
                  {/* Delete-monitoring opt-in + Import button — enabled when files selected */}
                  <div className="flex items-center gap-3">
                  <label
                    className="flex items-center gap-2 text-[12px] text-[#6B7280] cursor-pointer select-none"
                    title="Inhaltsänderungen und Umbenennungen werden nicht überwacht. Bei Aktivierung wird die Datei hier entfernt, wenn sie in SharePoint gelöscht wird."
                  >
                    <button
                      type="button"
                      role="switch"
                      aria-checked={importWatchDeletes}
                      onClick={() => setImportWatchDeletes(v => !v)}
                      className="relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors"
                      style={{ backgroundColor: importWatchDeletes ? aispaceColor : '#E5E7EB' }}
                    >
                      <span
                        className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform"
                        style={{ transform: importWatchDeletes ? 'translateX(18px)' : 'translateX(2px)' }}
                      />
                    </button>
                    Löschungen überwachen
                  </label>
                  <button
                    onClick={handleImportSelected}
                    disabled={Object.keys(selectedSpFiles).length === 0 || spImporting}
                    className="flex items-center gap-2 px-5 py-2.5 text-[14px] font-semibold text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ backgroundColor: aispaceColor }}
                  >
                    {spImporting ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        <span>{t('agentSettings.filesImporting')}</span>
                      </>
                    ) : (
                      <span>{Object.keys(selectedSpFiles).length > 0 ? t('agentSettings.filesImportWithCount', { count: Object.keys(selectedSpFiles).length }) : t('agentSettings.filesImport')}</span>
                    )}
                  </button>
                  </div>
                </div>
              )}

              {/* Table */}
              {browseLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 animate-spin" style={{ color: aispaceColor }} />
                </div>
              ) : (browseItems || []).length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-[#9CA3AF] text-[14px]">
                  <FolderClosed size={40} className="mb-3 text-[#D1D5DB]" />
                  {t('agentSettings.filesNoItems')}
                </div>
              ) : viewMode === 'grid' ? (
                /* Grid View */
                <div className="p-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-h-[480px] overflow-y-auto overflow-x-hidden">
                  {[...(browseItems || [])]
                    .sort((a, b) => {
                      if (a.type === 'folder' && b.type !== 'folder') return -1
                      if (a.type !== 'folder' && b.type === 'folder') return 1
                      return a.name.localeCompare(b.name)
                    })
                    .map((item) => {
                      const folderPath = getFolderPathForItem(item.name)
                      // A file is "synced" if its folder has auto-sync OR the file
                      // itself has an active SharePointFileMappings row for this
                      // task (backend annotates `is_imported`). Folders rely on
                      // the auto-sync check only.
                      const isSynced = isItemSynced(folderPath) || (item.type === 'file' && !!item.is_imported)
                      const hasChildSynced = item.type === 'folder' && !isSynced && hasSyncedDescendant(folderPath)
                      return (
                      <div
                        key={item.id}
                        onClick={() => item.type === 'folder' && navigateIntoFolder(item.name)}
                        className={`rounded-xl border p-5 hover:shadow-sm transition-all relative ${
                          item.type === 'folder' ? 'cursor-pointer' : ''
                        } ${isSynced ? 'bg-[#ECFDF5] border-green-300' : 'bg-white border-[#EEEEEE] hover:border-[#D1D5DB]'}`}
                      >
                        {/* Top: Icon + 3-dot menu (files only, not folders) */}
                        <div className="flex items-start justify-between mb-4">
                          <div
                            className="relative w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: isSynced ? '#A7F3D0' : `${aispaceColor}15` }}
                          >
                            {item.type === 'folder' ? (
                              <Folder size={20} style={{ color: isSynced ? '#059669' : aispaceColor }} />
                            ) : (
                              <FileText size={20} style={{ color: isSynced ? '#059669' : aispaceColor }} />
                            )}
                            {hasChildSynced && (
                              <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-500 border-2 border-white" />
                            )}
                          </div>
                          {/* Per-row 3-dot menu removed — it triggered folder/folder+subfolder
                              auto-sync, which is intentionally disabled. Use the Import button
                              at the top with the row checkbox for file-level sync. */}
                        </div>

                        {/* Name */}
                        <p className={`text-[14px] font-semibold mb-2 truncate ${isSynced ? 'text-[#065F46]' : 'text-[#111827]'}`} title={item.name}>
                          {item.name}
                        </p>

                        {/* Meta */}
                        <div className="space-y-1">
                          <p className="text-[12px] text-[#6B7280]">
                            {item.type === 'folder' ? t('agentSettings.filesItemTypeFolder') : t('agentSettings.filesItemTypeFile')}
                          </p>
                          <p className="text-[12px] text-[#9CA3AF]">
                            {t('agentSettings.filesMetaModified', { date: formatDate(item.lastModifiedDateTime) })}
                          </p>
                        </div>
                      </div>
                      )
                    })}
                </div>
              ) : (
                <div className="overflow-x-hidden">
                  {/* Table Header */}
                  <div className="flex items-center bg-[#F9FAFB] border-b border-[#EEEEEE]">
                    <div className="w-[40px] py-3 pl-4 flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={(browseItems || []).filter(i => i.type !== 'folder').length > 0 && (browseItems || []).filter(i => i.type !== 'folder').every(f => selectedSpFiles[f.id])}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-[#D1D5DB] cursor-pointer"
                        style={{ accentColor: aispaceColor }}
                      />
                    </div>
                    <div className="flex-1 py-3 px-3">
                      <span className="text-[13px] font-medium text-[#6B7280]">{t('agentSettings.filesColNameSp')}</span>
                    </div>
                    <div className="w-[120px] py-3 px-4">
                      <span className="text-[13px] font-medium text-[#6B7280]">{t('agentSettings.filesColTypeSp')}</span>
                    </div>
                    <div className="w-[140px] py-3 px-4">
                      <span className="text-[13px] font-medium text-[#6B7280]">{t('agentSettings.filesColCreated')}</span>
                    </div>
                    <div className="w-[140px] py-3 px-4">
                      <span className="text-[13px] font-medium text-[#6B7280]">{t('agentSettings.filesColModified')}</span>
                    </div>
                  </div>

                  {/* Table Rows - scrollable */}
                  <div className="max-h-[480px] overflow-y-auto overflow-x-hidden">
                  {[...(browseItems || [])]
                    .sort((a, b) => {
                      if (a.type === 'folder' && b.type !== 'folder') return -1
                      if (a.type !== 'folder' && b.type === 'folder') return 1
                      return a.name.localeCompare(b.name)
                    })
                    .map((item, index, arr) => {
                      const folderPath = getFolderPathForItem(item.name)
                      // See note in grid view above — files use `is_imported`
                      // from the backend in addition to folder-level auto-sync.
                      const isSynced = isItemSynced(folderPath) || (item.type === 'file' && !!item.is_imported)
                      const hasChildSynced = item.type === 'folder' && !isSynced && hasSyncedDescendant(folderPath)
                      return (
                      <div
                        key={item.id}
                        onClick={() => {
                          if (item.type === 'folder') navigateIntoFolder(item.name)
                          else toggleSpFileSelect(item)
                        }}
                        className={`flex items-center transition-colors cursor-pointer ${
                          selectedSpFiles[item.id] ? 'bg-[#F5F3FF]' : ''
                        } ${isSynced ? 'bg-[#ECFDF5] hover:bg-[#D1FAE5]' : 'hover:bg-[#F9FAFB]'} ${index !== arr.length - 1 ? 'border-b border-[#EEEEEE]' : ''}`}
                      >
                        {/* Checkbox for files */}
                        <div className="w-[40px] py-4 pl-4 flex-shrink-0">
                          {item.type !== 'folder' && (
                            <input
                              type="checkbox"
                              checked={!!selectedSpFiles[item.id]}
                              onChange={() => toggleSpFileSelect(item)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 rounded border-[#D1D5DB] cursor-pointer"
                              style={{ accentColor: aispaceColor }}
                            />
                          )}
                        </div>
                        <div className="flex-1 py-4 px-3 min-w-0">
                          <div className="flex items-center gap-3 min-w-0">
                            <div
                              className="relative w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: isSynced ? '#A7F3D0' : `${aispaceColor}15` }}
                            >
                              {item.type === 'folder' ? (
                                <Folder size={18} style={{ color: isSynced ? '#059669' : aispaceColor }} />
                              ) : (
                                <FileText size={18} style={{ color: isSynced ? '#059669' : aispaceColor }} />
                              )}
                              {hasChildSynced && (
                                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-500 border-2 border-white" />
                              )}
                            </div>
                            <span className={`text-[14px] font-medium truncate ${isSynced ? 'text-[#065F46]' : 'text-[#111827]'}`} title={item.name}>{item.name}</span>
                          </div>
                        </div>
                        <div className="w-[120px] py-4 px-4">
                          <span className="text-[14px] text-[#6B7280]">
                            {item.type === 'folder' ? t('agentSettings.filesColSpFolder') : t('agentSettings.filesColSpFile')}
                          </span>
                        </div>
                        <div className="w-[140px] py-4 px-4">
                          <span className="text-[14px] text-[#6B7280]">{formatDate(item.createdDateTime)}</span>
                        </div>
                        <div className="w-[140px] py-4 px-4">
                          <span className="text-[14px] text-[#6B7280]">{formatDate(item.lastModifiedDateTime)}</span>
                        </div>
                        {/* Per-row 3-dot menu cell removed — it triggered folder/folder+subfolder
                            auto-sync, which is intentionally disabled. */}
                      </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {(configuredSites || []).length > 0 && (
              <div className="flex items-start gap-2 px-4 py-3 mb-5 bg-[#F5F3FF] border border-[#E9E5FF] rounded-lg text-[13px] text-[#5B21B6]">
                <Info size={16} className="flex-shrink-0 mt-0.5" />
                <span>{t('agentSettings.filesImportInfoBanner')}</span>
              </div>
            )}
            {(!spSitesFetched || configuredSitesLoading) ? (
          <div className="bg-white rounded-xl border border-[#EEEEEE] flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: aispaceColor }} />
          </div>
        ) : (configuredSites || []).length === 0 ? (
          <div className="bg-white rounded-xl border border-[#EEEEEE] flex flex-col items-center justify-center py-20">
            <div className="w-16 h-16 bg-[#F3F4F6] rounded-xl flex items-center justify-center mb-5">
              <Globe size={32} className="text-[#9CA3AF]" />
            </div>
            <h3 className="text-[16px] font-semibold text-[#111827] mb-2">{t('agentSettings.filesNoConnectedSites')}</h3>
            <p className="text-[14px] text-[#6B7280] text-center max-w-[360px] mb-5">
              {t('agentSettings.filesNoConnectedSitesHint')}
            </p>
            <button
              onClick={() => hasSpConnection && taskOrganizationId && setAddSiteModalOpen(true)}
              disabled={!hasSpConnection || !taskOrganizationId}
              className="px-5 py-2.5 text-white rounded-lg text-[14px] font-semibold hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: aispaceColor }}
            >
              {t('agentSettings.filesAddSiteButton')}
            </button>
          </div>
        ) : (
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 lg:grid-cols-2 gap-4' : 'space-y-4'}>
            {(configuredSites || []).map((site) => {
              const isImporting = staticSyncPhase === 'running' && staticSyncSite?.id === site.id
              const anyImportRunningForTask = staticSyncPhase === 'running'
              return (
              <div key={site.id} className="bg-white border border-[#EEEEEE] rounded-xl p-5">
                {/* Top: name + status badge, actions */}
                <div className="flex items-start justify-between gap-4 mb-1">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${aispaceColor}15` }}>
                      <Globe size={20} className="text-[#6B7280]" />
                    </div>
                    <p className="text-[16px] font-semibold text-[#111827] truncate">{site.site_name}</p>
                    {(siteStats[site.id]?.files_imported || 0) > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-semibold bg-[#ECFDF5] text-[#059669] flex-shrink-0">
                        <CheckCircle2 size={12} />{t('agentSettings.filesStatusImported')}
                      </span>
                    )}
                    {siteStats[site.id]?.sources_changed && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-semibold bg-[#FEF3C7] text-[#D97706] flex-shrink-0"
                        title={t('agentSettings.filesSourcesChangedHint')}
                      >
                        <AlertTriangle size={12} />{t('agentSettings.filesSourcesChangedBadge')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 relative">
                    <button
                      onClick={() => setImportedFilesBrowserSite(site)}
                      disabled={isImporting}
                      className="flex items-center gap-1 px-4 py-2 text-[13px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:hover:bg-white"
                    >
                      <Folder size={14} />
                      {t('agentSettings.filesOpenSite')}
                    </button>
                    <button
                      onClick={() => setEditSourcesSite(site)}
                      disabled={isImporting}
                      className="flex items-center gap-1 px-4 py-2 text-[13px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:hover:bg-white"
                    >
                      {t('agentSettings.filesEditSources')}
                    </button>
                    <button
                      onClick={() => handleSyncAll(site)}
                      disabled={syncAllLoading || anyImportRunningForTask}
                      className="flex items-center gap-1 px-3 py-2 text-[13px] font-medium text-white rounded-lg transition-colors disabled:opacity-50"
                      style={{ backgroundColor: isImporting ? `${aispaceColor}99` : aispaceColor }}
                    >
                      {isImporting || (syncAllLoading && syncAllSite?.id === site.id)
                        ? <Loader2 size={13} className="animate-spin" />
                        : <RefreshCw size={13} />}
                      {isImporting
                        ? t('agentSettings.filesImporting')
                        : ((siteStats[site.id]?.files_imported || 0) > 0
                          ? t('agentSettings.filesImportAgain')
                          : t('agentSettings.filesStartImportSubmit'))}
                    </button>
                    <button
                      onClick={() => setSiteCardMenu(siteCardMenu === site.id ? null : site.id)}
                      disabled={isImporting}
                      className="p-2 border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50"
                    >
                      <MoreHorizontal size={16} className="text-[#6B7280]" />
                    </button>
                    {siteCardMenu === site.id && (
                      <div className="absolute right-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-xl shadow-lg z-20 min-w-[200px] p-1.5">
                        <button
                          onClick={() => { setSiteCardMenu(null); setUnsyncAllSite(site) }}
                          disabled={unsyncAllLoading}
                          className="w-full px-3 py-2 flex items-center gap-2 rounded-lg text-left text-[14px] text-[#EF4444] hover:bg-[#FEF2F2] transition-colors disabled:opacity-50"
                        >
                          {t('agentSettings.filesDeleteImportedData')}
                        </button>
                        <button
                          onClick={() => { setSiteCardMenu(null); setRemoveSiteConfirmError(null); setRemoveSiteConfirm(site) }}
                          disabled={removingSiteId === site.id}
                          className="w-full px-3 py-2 flex items-center gap-2 rounded-lg text-left text-[14px] text-[#EF4444] hover:bg-[#FEF2F2] transition-colors disabled:opacity-50"
                        >
                          {removingSiteId === site.id && <Loader2 size={14} className="animate-spin" />}
                          {t('agentSettings.filesRemoveSiteFromAgent')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* URL */}
                <div className="mb-4 pl-[52px]">
                  <a
                    href={site.site_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[13px] break-all hover:underline text-[#6B7280]"
                  >
                    <span className="truncate">{site.site_url}</span>
                    <ExternalLink size={12} className="flex-shrink-0" />
                  </a>
                </div>

                {/* Import stats */}
                <div className="bg-[#F9FAFB] rounded-lg border border-[#EEEEEE]">
                  <div className="grid grid-cols-3 divide-x divide-[#EEEEEE] px-4 py-3">
                    <button
                      onClick={() => (siteStats[site.id]?.files_imported > 0) && setImportedFilesBrowserSite(site)}
                      disabled={!(siteStats[site.id]?.files_imported > 0)}
                      className="text-left disabled:cursor-default"
                    >
                      {siteStats[site.id]?.loading || !siteStats[site.id] ? (
                        <Loader2 size={16} className="animate-spin" style={{ color: aispaceColor }} />
                      ) : (
                        <p className={`text-[22px] font-bold leading-none ${siteStats[site.id]?.files_imported > 0 ? 'hover:underline' : ''}`} style={{ color: siteStats[site.id]?.files_imported > 0 ? aispaceColor : '#111827' }}>
                          {siteStats[site.id]?.files_imported ?? 0}
                        </p>
                      )}
                      <p className="text-[12px] text-[#6B7280] mt-1">{t('agentSettings.filesStatsFilesImported')}</p>
                    </button>
                    <div className="pl-4">
                      <p className="text-[14px] font-semibold text-[#111827] truncate">
                        {(siteStats[site.id]?.sources && siteStats[site.id].sources.length > 0)
                          ? siteStats[site.id].sources.join(', ')
                          : '—'}
                      </p>
                      <p className="text-[12px] text-[#6B7280] mt-1">
                        {(siteStats[site.id]?.sources?.length || 0) === 1
                          ? t('agentSettings.filesStatsSourceCountSingular', { count: 1 })
                          : t('agentSettings.filesStatsSourceCountPlural', { count: siteStats[site.id]?.sources?.length || 0 })}
                      </p>
                    </div>
                    <div className="pl-4">
                      <p className="text-[14px] font-semibold text-[#111827]">
                        {formatLastImportDateTime(siteStats[site.id]?.last_import_at) || t('agentSettings.filesLastSyncNever')}
                      </p>
                      <p className="text-[12px] text-[#6B7280] mt-1">{t('agentSettings.filesStatsLastImport')}</p>
                    </div>
                  </div>
                  {siteStats[site.id]?.has_report && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-[#EEEEEE]">
                      <p className="text-[13px] text-[#6B7280]">
                        {t('agentSettings.filesStatsSkippedFailed', { skipped: siteStats[site.id]?.skipped || 0, failed: siteStats[site.id]?.failed || 0 })}
                      </p>
                      <button
                        onClick={() => setImportReportModal({
                          reportId: siteStats[site.id]?.report_id,
                          sources: siteStats[site.id]?.sources,
                          site,
                          fallbackStats: {
                            filesImported: siteStats[site.id]?.files_imported || 0,
                            skipped: siteStats[site.id]?.skipped || 0,
                            failed: siteStats[site.id]?.failed || 0,
                            lastImportAt: siteStats[site.id]?.last_import_at,
                          },
                        })}
                        disabled={isImporting}
                        className="text-[13px] font-medium hover:underline disabled:opacity-50 disabled:cursor-default disabled:hover:no-underline"
                        style={{ color: aispaceColor }}
                      >
                        {t('agentSettings.filesViewImportReport')}
                      </button>
                    </div>
                  )}
                </div>

                {/* Inline import-in-progress box — replaces the old separate progress modal */}
                {isImporting && (
                  <div className="mt-3 bg-[#F9FAFB] rounded-lg border border-[#EEEEEE] px-4 py-3">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        {staticSyncConnectionLost ? (
                          <WifiOff size={14} className="text-amber-500" />
                        ) : (
                          <Loader2 size={14} className="animate-spin" style={{ color: aispaceColor }} />
                        )}
                        <span className="text-[13px] font-semibold text-[#111827]">
                          {staticSyncConnectionLost ? t('agentSettings.filesImportReconnecting') : t('agentSettings.filesImportRunning')}
                        </span>
                      </div>
                      <button
                        onClick={() => setCancelConfirmOpen(true)}
                        disabled={staticSyncCancelling || cancelRequestedForJobId === staticSyncJobId}
                        className="text-[12px] font-medium text-[#DC2626] hover:underline disabled:opacity-50 disabled:cursor-default disabled:hover:no-underline"
                      >
                        {cancelRequestedForJobId === staticSyncJobId
                          ? t('agentSettings.filesImportCancelling')
                          : t('agentSettings.filesImportCancel')}
                      </button>
                    </div>
                    {staticSyncProgress && staticSyncProgress.total > 0 ? (
                      <>
                        <div className="flex justify-between text-[12px] text-[#6B7280] mb-1.5">
                          <span>{t('agentSettings.filesImportProcessedOf', { processed: staticSyncProgress.processed, total: staticSyncProgress.total })}</span>
                          <span>{Math.round((staticSyncProgress.processed / staticSyncProgress.total) * 100)}%</span>
                        </div>
                        <div className="w-full bg-[#E5E7EB] rounded-full h-2">
                          <div
                            className="h-2 rounded-full transition-all duration-500"
                            style={{ width: `${Math.round((staticSyncProgress.processed / staticSyncProgress.total) * 100)}%`, backgroundColor: aispaceColor }}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="w-full bg-[#E5E7EB] rounded-full h-2 overflow-hidden">
                        <div className="h-2 rounded-full animate-pulse" style={{ width: '40%', backgroundColor: aispaceColor }} />
                      </div>
                    )}
                  </div>
                )}
              </div>
              )
            })}
          </div>
            )}
          </>
        )
      ) : defaultFilesLoading ? (
        <div className="bg-white rounded-xl border border-[#EEEEEE] flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: aispaceColor }} />
        </div>
      ) : displayItems.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#EEEEEE] flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 bg-[#F3F4F6] rounded-xl flex items-center justify-center mb-5">
            <FolderClosed size={32} className="text-[#9CA3AF]" />
          </div>
          <h3 className="text-[16px] font-semibold text-[#111827] mb-2">
            {selectedFolder ? t('agentSettings.filesEmptyInFolder') : t('agentSettings.filesEmptyFolders')}
          </h3>
          <p className="text-[14px] text-[#6B7280] text-center max-w-[320px]">
            {activeTab === 'local'
              ? t('agentSettings.filesEmptyLocalHint')
              : t('agentSettings.filesEmptySharePointHint')
            }
          </p>
        </div>
      ) : viewMode === 'grid' && !selectedFolder ? (
        /* Grid View for Folders */
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {paginatedItems.map((folder) => (
              <div
                key={folder.name}
                onClick={() => handleFolderClick(folder)}
                className="bg-white rounded-xl border border-[#EEEEEE] p-5 hover:border-[#D1D5DB] hover:shadow-sm cursor-pointer transition-all relative"
              >
                {/* Top row: Folder icon + Action menu */}
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${aispaceColor}15` }}
                  >
                    <Folder size={20} style={{ color: aispaceColor }} />
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setActiveMenu(activeMenu === folder.name ? null : folder.name)
                    }}
                    className="p-1.5 border border-[#E5E7EB] hover:bg-[#F3F4F6] rounded-lg transition-colors"
                  >
                    <MoreHorizontal size={16} className="text-[#6B7280]" />
                  </button>
                </div>

                {/* Folder Name */}
                <h3 className="text-[16px] font-semibold text-[#111827] mb-2 truncate">
                  {folder.name}
                </h3>

                {/* File Count */}
                <p className="text-[14px] text-[#6B7280] mb-3">
                  {folder.files.length} {folder.files.length === 1 ? t('agentSettings.filesCountSingular') : t('agentSettings.filesCountPlural')}
                </p>

                {/* Dates */}
                <div className="space-y-1 text-[13px] text-[#9CA3AF]">
                  <p>{t('agentSettings.filesCreatedOn', { date: formatDate(folder.created_at) })}</p>
                  <p>{t('agentSettings.filesModifiedOn', { date: formatRelativeTime(folder.updated_at) })}</p>
                </div>

                {activeMenu === folder.name && (
                  <div
                    className="absolute right-3 top-12 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-20 min-w-[120px] overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => {
                        setActiveMenu(null)
                        setDeleteConfirm({ isOpen: true, type: 'folder', file: null, folder })
                      }}
                      className="w-full px-4 py-2.5 text-left text-[14px] text-red-600 hover:bg-[#F9FAFB] flex items-center gap-2"
                    >
                      <Trash2 size={16} />
                      <span>{t('agentSettings.filesDelete')}</span>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination for Grid */}
          {displayItems.length > itemsPerPage && (
            <div className="flex items-center justify-between mt-6">
              <span className="text-[14px] text-[#6B7280]">
                {t('agentSettings.filesPageOf', { current: currentPage, total: totalPages || 1 })}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-[14px] font-medium text-[#374151] border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] disabled:opacity-50 disabled:cursor-not-allowed transition-colors bg-white"
                >
                  {t('agentSettings.filesPrevious')}
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage >= totalPages}
                  className="px-4 py-2 text-[14px] font-medium text-[#374151] border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] disabled:opacity-50 disabled:cursor-not-allowed transition-colors bg-white"
                >
                  {t('agentSettings.filesNext')}
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        /* List View (Table) */
        <div className="bg-white rounded-xl border border-[#EEEEEE] overflow-hidden">
          {/* Table Header */}
          <div className="flex items-center bg-[#F9FAFB] border-b border-[#EEEEEE]">
            <div className="flex-1 py-3 px-6">
              <span className="text-[13px] font-medium text-[#6B7280]">
                {selectedFolder ? t('agentSettings.filesColFileName') : t('agentSettings.filesColFolderName')}
              </span>
            </div>
            {selectedFolder ? (
              <>
                <div className="w-[160px] py-3 px-4">
                  <span className="text-[13px] font-medium text-[#6B7280]">{t('agentSettings.filesColUploadedAt')}</span>
                </div>
                <div className="w-[120px] py-3 px-4">
                  <span className="text-[13px] font-medium text-[#6B7280]">{t('agentSettings.filesColSize')}</span>
                </div>
                <div className="w-[140px] py-3 px-4">
                  <span className="text-[13px] font-medium text-[#6B7280]">{t('agentSettings.filesColUsedIn')}</span>
                </div>
              </>
            ) : (
              <>
                <div className="w-[140px] py-3 px-4">
                  <span className="text-[13px] font-medium text-[#6B7280]">{t('agentSettings.filesColFiles')}</span>
                </div>
                <div className="w-[140px] py-3 px-4">
                  <span className="text-[13px] font-medium text-[#6B7280]">{t('agentSettings.filesColCreatedAt')}</span>
                </div>
                <div className="w-[140px] py-3 px-4">
                  <span className="text-[13px] font-medium text-[#6B7280]">{t('agentSettings.filesColLastModified')}</span>
                </div>
              </>
            )}
            <div className="w-[60px] py-3 px-4"></div>
          </div>

          {/* Table Body */}
          {selectedFolder ? (
            // File rows
            paginatedItems.map((file, index) => (
              <div
                key={file.id}
                className={`flex items-center hover:bg-[#F9FAFB] ${
                  index !== paginatedItems.length - 1 ? 'border-b border-[#EEEEEE]' : ''
                }`}
              >
                <div className="flex-1 py-4 px-6">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${aispaceColor}15` }}
                    >
                      <FileText size={18} style={{ color: aispaceColor }} />
                    </div>
                    <div>
                      <span className="text-[14px] font-medium text-[#111827] block">
                        {file.filename}
                      </span>
                      <span className="text-[12px] text-[#9CA3AF]">
                        {formatFileSize(file.file_size)}
                      </span>
                    </div>
                    {file.sync_status === 'deleted_in_source' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-orange-100 text-orange-700">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        {t('agentSettings.filesSourceDeleted')}
                      </span>
                    )}
                  </div>
                </div>

                <div className="w-[160px] py-4 px-4">
                  <span className="text-[14px] text-[#374151]">
                    {formatDate(file.created_at)}
                  </span>
                </div>

                <div className="w-[120px] py-4 px-4">
                  <span className="text-[14px] text-[#374151]">
                    {formatFileSize(file.file_size)}
                  </span>
                </div>

                <div className="w-[140px] py-4 px-4">
                  <span className="text-[14px] text-[#374151]">
                    {file.usage_count || 1} {(file.usage_count || 1) === 1 ? t('agentSettings.filesProjectSingular') : t('agentSettings.filesProjectPlural')}
                  </span>
                </div>

                <div className="w-[60px] py-4 px-4 relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setActiveMenu(activeMenu === file.id ? null : file.id)
                    }}
                    className="p-1.5 border border-[#E5E7EB] hover:bg-[#F3F4F6] rounded-lg transition-colors"
                  >
                    <MoreHorizontal size={18} className="text-[#6B7280]" />
                  </button>

                  {activeMenu === file.id && (
                    <div
                      className="absolute right-4 top-12 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-20 min-w-[120px] overflow-hidden"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() => {
                          setActiveMenu(null)
                          setDeleteConfirm({ isOpen: true, type: 'file', file, folder: null })
                        }}
                        disabled={deletingFileId === file.id}
                        className="w-full px-4 py-2.5 text-left text-[14px] text-red-600 hover:bg-[#F9FAFB] flex items-center gap-2 disabled:opacity-50"
                      >
                        {deletingFileId === file.id ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Trash2 size={16} />
                        )}
                        <span>{t('agentSettings.filesDelete')}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            // Folder rows
            paginatedItems.map((folder, index) => (
              <div
                key={folder.name}
                onClick={() => handleFolderClick(folder)}
                className={`flex items-center hover:bg-[#F9FAFB] cursor-pointer ${
                  index !== paginatedItems.length - 1 ? 'border-b border-[#EEEEEE]' : ''
                }`}
              >
                <div className="flex-1 py-4 px-6">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${aispaceColor}15` }}
                    >
                      <Folder size={18} style={{ color: aispaceColor }} />
                    </div>
                    <span className="text-[14px] font-medium text-[#111827]">
                      {folder.name}
                    </span>
                  </div>
                </div>

                <div className="w-[140px] py-4 px-4">
                  <span className="text-[14px] text-[#374151]">
                    {folder.files.length} {folder.files.length === 1 ? t('agentSettings.filesCountSingular') : t('agentSettings.filesCountPlural')}
                  </span>
                </div>

                <div className="w-[140px] py-4 px-4">
                  <span className="text-[14px] text-[#374151]">
                    {formatDate(folder.created_at)}
                  </span>
                </div>

                <div className="w-[140px] py-4 px-4">
                  <span className="text-[14px] text-[#374151]">
                    {formatRelativeTime(folder.updated_at)}
                  </span>
                </div>

                <div className="w-[60px] py-4 px-4 relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setActiveMenu(activeMenu === folder.name ? null : folder.name)
                    }}
                    className="p-1.5 border border-[#E5E7EB] hover:bg-[#F3F4F6] rounded-lg transition-colors"
                  >
                    <MoreHorizontal size={18} className="text-[#6B7280]" />
                  </button>

                  {activeMenu === folder.name && (
                    <div
                      className="absolute right-4 top-12 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-20 min-w-[120px] overflow-hidden"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() => {
                          setActiveMenu(null)
                          setDeleteConfirm({ isOpen: true, type: 'folder', file: null, folder })
                        }}
                        className="w-full px-4 py-2.5 text-left text-[14px] text-red-600 hover:bg-[#F9FAFB] flex items-center gap-2"
                      >
                        <Trash2 size={16} />
                        <span>{t('agentSettings.filesDelete')}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}

          {/* Pagination */}
          {displayItems.length > 0 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-[#EEEEEE]">
              <span className="text-[14px] text-[#6B7280]">
                {t('agentSettings.filesPageOf', { current: currentPage, total: totalPages || 1 })}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-[14px] font-medium text-[#374151] border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {t('agentSettings.filesPrevious')}
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage >= totalPages}
                  className="px-4 py-2 text-[14px] font-medium text-[#374151] border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {t('agentSettings.filesNext')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add Folder Modal */}
      {showFolderModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-[560px] overflow-hidden shadow-xl mx-4">
            <div className="relative px-8 pt-8 pb-6">
              <button
                onClick={handleCloseModal}
                className="absolute top-6 right-6 p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors"
              >
                <X size={20} className="text-[#6B7280]" />
              </button>

              <div className="flex items-center gap-4 mb-3">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${aispaceColor}15` }}
                >
                  {selectedFolder ? (
                    <Upload size={24} style={{ color: aispaceColor }} />
                  ) : (
                    <Folder size={24} style={{ color: aispaceColor }} />
                  )}
                </div>
                <div>
                  <h2 className="text-[20px] font-semibold text-[#111827]">
                    {selectedFolder ? t('agentSettings.filesAddToFolderTitle', { folderName: selectedFolder.name }) : t('agentSettings.filesCreateNewFolderTitle')}
                  </h2>
                  <p className="text-[14px] text-[#6B7280]">
                    {selectedFolder
                      ? t('agentSettings.filesAddToFolderSubtitle')
                      : t('agentSettings.filesCreateNewFolderSubtitle')}
                  </p>
                </div>
              </div>
            </div>

            <div className="px-8 pb-6">
              {!selectedFolder && (
                <div className="mb-5">
                  <label className="block text-[14px] font-semibold text-[#111827] mb-2">
                    {t('agentSettings.filesFolderNameLabel')}
                  </label>
                  <input
                    type="text"
                    value={folderName}
                    onChange={(e) => setFolderName(e.target.value)}
                    placeholder={t('agentSettings.filesFolderNamePlaceholder')}
                    className="w-full px-4 py-3 text-[14px] text-[#374151] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[var(--focus-color)] transition-colors placeholder:text-[#9CA3AF]"
                    style={{ '--focus-color': aispaceColor }}
                  />
                </div>
              )}

              <div className="mb-5">
                <label className="block text-[14px] font-semibold text-[#111827] mb-2">
                  {t('agentSettings.filesAddFilesLabel')}
                </label>
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                    isDragging
                      ? 'border-[var(--drag-color)] bg-[var(--drag-bg)]'
                      : 'border-[#E5E7EB] hover:border-[var(--hover-color)] hover:bg-[#F9FAFB]'
                  }`}
                  style={{
                    '--drag-color': aispaceColor,
                    '--drag-bg': `${aispaceColor}10`,
                    '--hover-color': aispaceColor
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: `${aispaceColor}15` }}
                  >
                    <Upload size={24} style={{ color: aispaceColor }} />
                  </div>
                  <p className="text-[14px] font-medium text-[#374151] mb-1">
                    {t('agentSettings.filesDragOrClick')}
                  </p>
                  <p className="text-[13px] text-[#9CA3AF]">
                    {t('agentSettings.filesSupportedTypes')}
                  </p>
                </div>
              </div>

              {selectedFiles.length > 0 && (
                <div className="mb-2">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[14px] font-semibold text-[#111827]">
                      {t('agentSettings.filesSelectedFiles', { count: selectedFiles.length })}
                    </span>
                    <button
                      onClick={() => setSelectedFiles([])}
                      className="text-[13px] text-[#6B7280] hover:text-[#374151] transition-colors"
                    >
                      {t('agentSettings.filesRemoveAll')}
                    </button>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto space-y-2">
                    {selectedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-[#F9FAFB] rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${aispaceColor}15` }}
                          >
                            <FileText size={16} style={{ color: aispaceColor }} />
                          </div>
                          <div>
                            <span className="text-[13px] font-medium text-[#111827] block truncate max-w-[300px]">
                              {file.name}
                            </span>
                            <span className="text-[12px] text-[#9CA3AF]">
                              {formatFileSize(file.size)}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => removeFileFromSelection(index)}
                          className="p-1 hover:bg-[#E5E7EB] rounded transition-colors"
                        >
                          <X size={16} className="text-[#6B7280]" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="px-8 py-5 bg-[#F9FAFB] flex justify-end gap-3">
              <button
                onClick={handleCloseModal}
                className="px-6 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6] transition-colors"
              >
                {t('agentSettings.filesCancel')}
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={!folderName.trim() || selectedFiles.length === 0 || uploading}
                className="px-6 py-2.5 text-[14px] font-medium text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                style={{ backgroundColor: aispaceColor }}
              >
                {uploading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>{selectedFolder ? t('agentSettings.filesUploadingStatus') : t('agentSettings.filesCreatingStatus')}</span>
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    <span>{selectedFolder ? t('agentSettings.filesUpload') : t('agentSettings.filesCreateFolder')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Progress Modal */}
      {uploading && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl w-full max-w-[400px] overflow-hidden shadow-xl mx-4 p-6">
            <h3 className="text-[18px] font-semibold text-[#111827] mb-4">
              {selectedFolder ? t('agentSettings.filesUploadProgressUploading') : t('agentSettings.filesUploadProgressCreating')}
            </h3>

            <div className="space-y-4">
              <div className="w-full bg-[#E5E7EB] rounded-full h-2">
                <div
                  className="h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${uploadProgress}%`, backgroundColor: aispaceColor }}
                ></div>
              </div>

              <div className="text-center">
                <p className="text-[14px] text-[#6B7280]">
                  {currentFileIndex > 0 && totalFiles > 0 ? (
                    t('agentSettings.filesFileOfTotal', { current: currentFileIndex, total: totalFiles })
                  ) : (
                    t('agentSettings.filesPreparing')
                  )}
                </p>
                {currentFileName && (
                  <p className="text-[13px] text-[#9CA3AF] mt-1 truncate">
                    {currentFileName}
                  </p>
                )}
                <p className="text-[14px] font-medium text-[#111827] mt-2">
                  {t('agentSettings.filesPercentDone', { percent: Math.round(uploadProgress) })}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.isOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              if (isDeleting) return
              setDeleteConfirm({ isOpen: false, type: null, file: null, folder: null, spItem: null })
            }}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-[400px] p-6 mx-4">
            {/* Warning Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-[#FEE2E2] flex items-center justify-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#EF4444" strokeWidth="2"/>
                  <path d="M12 8V12" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="12" cy="16" r="1" fill="#EF4444"/>
                </svg>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-[18px] font-semibold text-[#111827] text-center mb-2">
              {deleteConfirm.type === 'folder'
                ? t('agentSettings.filesDeleteFolderTitle')
                : deleteConfirm.type === 'sp-item'
                  ? (deleteConfirm.spItem?.type === 'folder' ? t('agentSettings.filesDeleteFolderTitle') : t('agentSettings.filesDeleteFileTitle'))
                  : t('agentSettings.filesDeleteFileTitle')}
            </h3>

            {/* Description */}
            <p className="text-[14px] text-[#6B7280] text-center mb-6">
              {deleteConfirm.type === 'folder'
                ? t('agentSettings.filesDeleteFolderMessage', { name: deleteConfirm.folder?.name, count: deleteConfirm.folder?.files.length })
                : deleteConfirm.type === 'sp-item'
                  ? t('agentSettings.filesDeleteSpItemMessage', { name: deleteConfirm.spItem?.name })
                  : t('agentSettings.filesDeleteFileMessage', { name: deleteConfirm.file?.filename })}
            </p>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm({ isOpen: false, type: null, file: null, folder: null, spItem: null })}
                disabled={isDeleting}
                className="flex-1 px-4 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('agentSettings.filesCancel')}
              </button>
              <button
                onClick={confirmDeleteAction}
                disabled={isDeleting}
                className="flex-1 px-4 py-2.5 text-[14px] font-medium text-white bg-[#EF4444] rounded-lg hover:bg-[#DC2626] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {t('agentSettings.filesDeleting')}
                  </>
                ) : (
                  t('agentSettings.filesDelete')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Import Confirmation Modal */}
      {cancelConfirmOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              if (cancellingAction) return
              setCancelConfirmOpen(false)
            }}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-[420px] p-6 mx-4">
            {!!cancellingAction && (
              <div className="absolute inset-0 z-10 rounded-xl bg-white/80 flex flex-col items-center justify-center gap-2">
                <Loader2 size={28} className="animate-spin" style={{ color: aispaceColor }} />
                <span className="text-[13px] font-medium text-[#374151]">{t('agentSettings.filesImportCancelProcessing')}</span>
              </div>
            )}
            <button
              onClick={() => setCancelConfirmOpen(false)}
              disabled={!!cancellingAction}
              className="absolute top-4 right-4 p-1 rounded-lg text-[#9CA3AF] hover:bg-[#F3F4F6] hover:text-[#374151] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label={t('agentSettings.filesImportCancelBack')}
            >
              <X size={18} />
            </button>

            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-[#FEE2E2] flex items-center justify-center">
                <AlertTriangle size={22} className="text-[#EF4444]" />
              </div>
            </div>

            <h3 className="text-[18px] font-semibold text-[#111827] text-center mb-2">
              {t('agentSettings.filesImportCancelTitle')}
            </h3>

            <p className="text-[14px] text-[#6B7280] text-center mb-6">
              {t('agentSettings.filesImportCancelMessage')}
            </p>

            <div className="flex gap-2">
              <button
                onClick={() => handleCancelStaticSync('stop')}
                disabled={!!cancellingAction}
                className="flex-1 px-4 py-2.5 text-[14px] font-medium text-white bg-[#111827] rounded-lg hover:bg-[#1F2937] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {cancellingAction === 'stop' && <Loader2 size={16} className="animate-spin" />}
                {t('agentSettings.filesImportCancelStop')}
              </button>
              <button
                onClick={() => handleCancelStaticSync('discard')}
                disabled={!!cancellingAction}
                className="flex-1 px-4 py-2.5 text-[14px] font-medium text-white bg-[#EF4444] rounded-lg hover:bg-[#DC2626] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {cancellingAction === 'discard' && <Loader2 size={16} className="animate-spin" />}
                {t('agentSettings.filesImportCancelDiscard')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Modal for All Files tab */}
      {filterModalOpen && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[70]" onClick={() => setFilterModalOpen(false)}>
          <div className="bg-white rounded-2xl w-[560px] shadow-xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E7EB] rounded-t-2xl bg-white">
              <h2 className="text-[20px] font-bold text-[#111827]">{t('agentSettings.filesFilterTitle')}</h2>
              <button
                onClick={() => setFilterModalOpen(false)}
                className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors"
              >
                <X size={20} className="text-[#6B7280]" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-5">
              {/* File Type */}
              <div>
                <p className="text-[14px] font-medium text-[#111827] mb-2">{t('agentSettings.filesFilterFileTypeLabel')}</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { id: 'local', label: t('agentSettings.filesFilterOptLocal') },
                    { id: 'sharepoint', label: t('agentSettings.filesFilterOptSharePoint') },
                  ].map((opt) => {
                    const selected = draftFileType === opt.id
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setDraftFileType(selected ? null : opt.id)}
                        className="px-5 py-4 rounded-xl border-2 text-[14px] font-semibold transition-colors"
                        style={selected
                          ? { backgroundColor: `${aispaceColor}1A`, borderColor: aispaceColor, color: aispaceColor }
                          : { borderColor: '#E5E7EB', color: '#111827', backgroundColor: 'white' }}
                      >
                        {opt.label}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Sort By */}
              <div>
                <p className="text-[14px] font-medium text-[#111827] mb-2">{t('agentSettings.filesFilterSortByLabel')}</p>
                <div className="relative">
                  {(() => {
                    const sortOptions = [
                      { id: 'newest', label: t('agentSettings.filesSortNewest') },
                      { id: 'oldest', label: t('agentSettings.filesSortOldest') },
                      { id: 'most_progress', label: t('agentSettings.filesSortMostProgress') },
                      { id: 'least_progress', label: t('agentSettings.filesSortLeastProgress') },
                    ]
                    const currentLabel = (sortOptions.find(o => o.id === draftSortBy) || sortOptions[0]).label
                    return (
                      <>
                        <button
                          type="button"
                          onClick={() => setFilterSortOpen(v => !v)}
                          className="w-full flex items-center justify-between px-4 py-3 border border-[#E5E7EB] rounded-xl bg-white hover:bg-[#F9FAFB] transition-colors"
                        >
                          <span className="text-[14px] text-[#111827]">{currentLabel}</span>
                          <ChevronDown size={18} className="text-[#6B7280]" />
                        </button>
                        {filterSortOpen && (
                          <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-xl shadow-lg overflow-hidden z-10">
                            {sortOptions.map(opt => (
                              <button
                                key={opt.id}
                                type="button"
                                onClick={() => { setDraftSortBy(opt.id); setFilterSortOpen(false) }}
                                className={`w-full text-left px-4 py-3 text-[14px] text-[#111827] hover:bg-[#F9FAFB] ${draftSortBy === opt.id ? 'font-semibold' : ''}`}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )
                  })()}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB] rounded-b-2xl">
              <button
                onClick={() => {
                  setDraftFileType(null)
                  setDraftSortBy('newest')
                  setFilterFileType(null)
                  setFilterSortBy('newest')
                  setAllFilesPage(1)
                  setFilterModalOpen(false)
                }}
                className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
              >
                {t('agentSettings.filesResetAll')}
              </button>
              <button
                onClick={() => {
                  setFilterFileType(draftFileType)
                  setFilterSortBy(draftSortBy)
                  setAllFilesPage(1)
                  setFilterModalOpen(false)
                }}
                className="px-6 py-2.5 text-[14px] font-semibold text-white rounded-lg hover:opacity-90 transition-colors"
                style={{ backgroundColor: aispaceColor }}
              >
                {t('agentSettings.filesApply')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SharePoint File Browser Modal */}
      <SharePointFileBrowser
        isOpen={showSpBrowser}
        onClose={() => setShowSpBrowser(false)}
        organizationId={taskOrganizationId}
        taskId={selectedTask?.id?.toString()}
        userOrganizationId={userOrganizationId}
      />

      {/* Sync All results dialog */}
      {showSyncAllResults && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-[520px] max-h-[80vh] overflow-y-auto shadow-xl p-6">
            <h3 className="text-[18px] font-bold text-[#111827] mb-3">Sync All abgeschlossen</h3>
            {syncAllError ? (
              <p className="text-sm text-red-600 mb-4">{syncAllError}</p>
            ) : (
              <p className="text-sm text-[#6B7280] mb-4">{syncAllResults?.message}</p>
            )}
            {(syncAllResults?.results || []).filter(r => r.status === 'synced').length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-green-700 mb-2">Erfolgreich importiert ({syncAllResults.results.filter(r => r.status === 'synced').length}):</p>
                <ul className="space-y-1 max-h-40 overflow-y-auto">
                  {syncAllResults.results.filter(r => r.status === 'synced').map((r, i) => (
                    <li key={i} className="flex items-center text-sm text-green-600">
                      <FileText size={14} className="mr-2 flex-shrink-0" />{r.file_name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(syncAllResults?.results || []).filter(r => r.status === 'skipped').length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-[#6B7280] mb-2">Übersprungen ({syncAllResults.results.filter(r => r.status === 'skipped').length}):</p>
                <ul className="space-y-1 max-h-32 overflow-y-auto">
                  {syncAllResults.results.filter(r => r.status === 'skipped').map((r, i) => (
                    <li key={i} className="text-sm text-[#9CA3AF] flex items-center">
                      <FileText size={14} className="mr-2 flex-shrink-0" />{r.file_name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(syncAllResults?.results || []).filter(r => r.status === 'error').length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-red-700 mb-2">Fehler ({syncAllResults.results.filter(r => r.status === 'error').length}):</p>
                <ul className="space-y-1 max-h-32 overflow-y-auto">
                  {syncAllResults.results.filter(r => r.status === 'error').map((r, i) => (
                    <li key={i} className="text-sm text-red-600">{r.file_name}: {r.error}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-end mt-4">
              <button
                onClick={() => { setShowSyncAllResults(false); setSyncAllSite(null) }}
                className="px-5 py-2 text-sm font-medium text-white rounded-lg"
                style={{ backgroundColor: aispaceColor }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sync All loading overlay */}
      {syncAllLoading && syncAllSite && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 w-[420px] text-center shadow-xl">
            <Loader2 size={40} className="animate-spin mx-auto mb-4" style={{ color: aispaceColor }} />
            <p className="text-[16px] font-semibold text-[#111827] mb-1">Sync All läuft...</p>
            <p className="text-sm text-[#6B7280] mb-3">
              Alle Dateien von <strong>{syncAllSite.site_name}</strong> werden importiert.
            </p>
            {syncAllProgress && syncAllProgress.total > 0 && (
              <div className="text-left">
                <div className="flex justify-between text-xs text-[#6B7280] mb-1">
                  <span>{syncAllProgress.processed} / {syncAllProgress.total} Dateien</span>
                  <span>{Math.round((syncAllProgress.processed / syncAllProgress.total) * 100)}%</span>
                </div>
                <div className="w-full bg-[#F3F4F6] rounded-full h-2 mb-2">
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{ width: `${Math.round((syncAllProgress.processed / syncAllProgress.total) * 100)}%`, backgroundColor: aispaceColor }}
                  />
                </div>
                {syncAllProgress.current_file && (
                  <p className="text-xs text-[#9CA3AF] truncate" title={syncAllProgress.current_file}>
                    {syncAllProgress.current_file}
                  </p>
                )}
              </div>
            )}
            {(!syncAllProgress || syncAllProgress.total === 0) && (
              <p className="text-xs text-[#9CA3AF]">Dateien werden gesammelt...</p>
            )}
          </div>
        </div>
      )}

      {/* Un-sync All confirmation dialog */}
      {unsyncAllSite && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-[440px] shadow-xl p-6">
            <h3 className="text-[18px] font-bold text-[#111827] mb-3">{t('agentSettings.filesDeleteImportedDataConfirmTitle')}</h3>
            <p className="text-sm text-[#6B7280] mb-4">
              {t('agentSettings.filesDeleteImportedDataConfirmMessage', { siteName: unsyncAllSite.site_name })}
            </p>
            {unsyncAllError && (
              <p className="text-sm text-red-600 mb-3">
                {unsyncAllError?.code === 'import_already_running'
                  ? t('agentSettings.filesRetryImportRunning')
                  : (typeof unsyncAllError === 'string' ? unsyncAllError : t('agentSettings.filesDeleteImportedDataError'))}
              </p>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setUnsyncAllSite(null)}
                disabled={unsyncAllLoading}
                className="px-4 py-2 text-sm font-medium text-[#374151] bg-[#F3F4F6] rounded-lg hover:bg-[#E5E7EB] disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleUnsyncAll}
                disabled={unsyncAllLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {unsyncAllLoading ? <><Loader2 size={14} className="animate-spin" />Entferne...</> : 'Alle entfernen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* "Remove site from agent" confirmation dialog — this deletes all
          imported data for the site (documents/embeddings/mappings) and
          clears its source selection, in addition to unassigning it. */}
      {removeSiteConfirm && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-[440px] shadow-xl p-6">
            <h3 className="text-[18px] font-bold text-[#111827] mb-3">{t('agentSettings.filesRemoveSiteConfirmTitle')}</h3>
            <p className="text-sm text-[#6B7280] mb-4">
              {t('agentSettings.filesRemoveSiteConfirmMessage', { siteName: removeSiteConfirm.site_name })}
            </p>
            {removeSiteConfirmError && <p className="text-sm text-red-600 mb-3">{removeSiteConfirmError}</p>}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setRemoveSiteConfirm(null)}
                disabled={removingSiteId === removeSiteConfirm.id}
                className="px-4 py-2 text-sm font-medium text-[#374151] bg-[#F3F4F6] rounded-lg hover:bg-[#E5E7EB] disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleRemoveSiteFromAgent}
                disabled={removingSiteId === removeSiteConfirm.id}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {removingSiteId === removeSiteConfirm.id && <Loader2 size={14} className="animate-spin" />}
                {t('agentSettings.filesRemoveSiteConfirmButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sync All Options Dialog ─────────────────────────────────────── */}
      {showSyncDialog && syncDialogSite && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-[480px] shadow-xl overflow-hidden">
            {/* Header */}
            <div className="px-6 py-5">
              <h3 className="text-[20px] font-bold text-[#111827]">{t('agentSettings.filesStartImportTitle')}</h3>
              <p className="text-[13px] text-[#6B7280] mt-1">
                {syncDialogSite.site_name}
                {(siteStats[syncDialogSite.id]?.sources || []).length > 0 && ` · ${siteStats[syncDialogSite.id].sources.join(', ')}`}
              </p>
            </div>

            <div className="px-6 pb-5 space-y-5">
              {/* File types */}
              <div>
                <p className="text-sm font-semibold text-[#374151] mb-2">{t('agentSettings.filesStartImportFileTypes')}</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: 'pdf', label: 'PDF' },
                    { key: 'docx', label: 'Word (.docx)' },
                    { key: 'doc',  label: 'Word (.doc)' },
                    { key: 'xlsx', label: 'Excel (.xlsx)' },
                    { key: 'xls',  label: 'Excel (.xls)' },
                    { key: 'pptx', label: 'PowerPoint (.pptx)' },
                    { key: 'ppt',  label: 'PowerPoint (.ppt)' },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => setSyncFileTypes(p => ({ ...p, [key]: !p[key] }))}
                      className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                        syncFileTypes[key]
                          ? 'text-white border-transparent'
                          : 'text-[#6B7280] bg-white border-[#E5E7EB] hover:bg-[#F9FAFB]'
                      }`}
                      style={syncFileTypes[key] ? { backgroundColor: aispaceColor, borderColor: aispaceColor } : {}}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Page limit */}
              <div>
                <p className="text-sm font-semibold text-[#374151] mb-2">{t('agentSettings.filesStartImportPageLimit')}</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 0,   label: t('agentSettings.filesStartImportNoLimit') },
                    { value: 10,  label: 'Max 10' },
                    { value: 50,  label: 'Max 50' },
                    { value: 250, label: 'Max 250' },
                    { value: 500, label: 'Max 500' },
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => setSyncMaxPages(value)}
                      className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                        syncMaxPages === value
                          ? 'text-white border-transparent'
                          : 'text-[#6B7280] bg-white border-[#E5E7EB] hover:bg-[#F9FAFB]'
                      }`}
                      style={syncMaxPages === value ? { backgroundColor: aispaceColor, borderColor: aispaceColor } : {}}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p className="text-[13px] text-[#9CA3AF] mt-2">{t('agentSettings.filesStartImportPageLimitHint')}</p>
              </div>

              {/* Replaces-existing-data warning — only if this site has been imported before */}
              {(siteStats[syncDialogSite.id]?.files_imported || 0) > 0 && (
                <div className="flex items-start gap-3 px-4 py-3 bg-[#FEF3C7] border border-[#FDE68A] rounded-lg">
                  <AlertTriangle size={18} className="text-[#D97706] flex-shrink-0 mt-0.5" />
                  <p className="text-[13px] text-[#92400E]">
                    <span className="font-semibold">
                      {t('agentSettings.filesStartImportReplaceWarningBold', { date: formatLastImportDateTime(siteStats[syncDialogSite.id]?.last_import_at) })}
                    </span>{' '}
                    {t('agentSettings.filesStartImportReplaceWarningRest', { count: siteStats[syncDialogSite.id]?.files_imported || 0 })}
                  </p>
                </div>
              )}

              {/* Sources added/removed since the last import for this site */}
              {siteStats[syncDialogSite.id]?.sources_changed && (
                <div className="flex items-start gap-3 px-4 py-3 bg-[#FEF3C7] border border-[#FDE68A] rounded-lg">
                  <AlertTriangle size={18} className="text-[#D97706] flex-shrink-0 mt-0.5" />
                  <div className="text-[13px] text-[#92400E] space-y-1">
                    {(siteStats[syncDialogSite.id]?.sources_added?.length || 0) > 0 && (
                      <p>
                        <span className="font-semibold">{t('agentSettings.filesStartImportSourcesAddedLabel')}</span>{' '}
                        {siteStats[syncDialogSite.id].sources_added.join(', ')}
                      </p>
                    )}
                    {(siteStats[syncDialogSite.id]?.sources_removed?.length || 0) > 0 && (
                      <p>
                        <span className="font-semibold">{t('agentSettings.filesStartImportSourcesRemovedLabel')}</span>{' '}
                        {siteStats[syncDialogSite.id].sources_removed.join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Static info — always shown */}
              <div className="flex items-start gap-3 px-4 py-3 bg-[#FEF9E7] border border-[#FDE68A] rounded-lg">
                <Info size={18} className="text-[#D97706] flex-shrink-0 mt-0.5" />
                <p className="text-[13px] text-[#92400E]">{t('agentSettings.filesStartImportInfoStatic')}</p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 pb-5">
              <button
                onClick={() => setShowSyncDialog(false)}
                className="px-5 py-2.5 text-sm font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB]"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSyncAllConfirm}
                disabled={!Object.values(syncFileTypes).some(Boolean)}
                className="px-5 py-2.5 text-sm font-medium text-white rounded-lg disabled:opacity-50"
                style={{ backgroundColor: aispaceColor }}
              >
                {t('agentSettings.filesStartImportSubmit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* "+ Add site" wizard (step 1: pick a site, step 2: pick sources) —
          reused for "Edit sources" on an already-added site via initialSite,
          which skips straight to step 2 pre-checked with the current selection. */}
      {(addSiteModalOpen || editSourcesSite) && (
        <AddSharePointSiteModal
          orgId={taskOrganizationId}
          taskId={taskIdStr}
          aispaceColor={aispaceColor}
          t={t}
          initialSite={editSourcesSite}
          onClose={() => { setAddSiteModalOpen(false); setEditSourcesSite(null) }}
          onAdded={() => {
            const wasEdit = !!editSourcesSite
            setAddSiteModalOpen(false)
            setEditSourcesSite(null)
            dispatch(fetchConfiguredSites({ orgId: taskOrganizationId, taskId: taskIdStr }))
            addToast(t(wasEdit ? 'agentSettings.filesSourcesUpdated' : 'agentSettings.filesSiteAddedToAgent'), 'success')
          }}
        />
      )}

      {scheduleModalOpen && (
        <SyncScheduleModal
          orgId={taskOrganizationId}
          taskId={taskIdStr}
          aispaceColor={aispaceColor}
          t={t}
          onClose={(saved) => {
            setScheduleModalOpen(false)
            if (saved) addToast(t('agentSettings.filesScheduleSyncSaveButton'), 'success')
          }}
        />
      )}

      {/* Browse already-imported files by folder — opened by clicking the "Files imported" stat */}
      {importedFilesBrowserSite && (
        <ImportedFilesBrowserModal
          orgId={taskOrganizationId}
          taskId={taskIdStr}
          site={importedFilesBrowserSite}
          aispaceColor={aispaceColor}
          t={t}
          onClose={() => setImportedFilesBrowserSite(null)}
        />
      )}

      {/* Import Report Modal — opened from a site card's "View import report" */}
      {importReportModal && (
        <ImportReportModal
          reportId={importReportModal.reportId}
          sources={importReportModal.sources}
          siteName={importReportModal.site?.site_name}
          fallbackStats={importReportModal.fallbackStats}
          aispaceColor={aispaceColor}
          t={t}
          onClose={() => setImportReportModal(null)}
          onRetryFailed={() => {
            const site = importReportModal.site
            const reportId = importReportModal.reportId
            setImportReportModal(null)
            if (site && reportId) handleRetryFailed(site, reportId)
          }}
        />
      )}

      {/* Deletion Report Modal — opened right after "Delete imported data"
          or "Remove site from agent" completes. No persistent card entry
          point for this one: "Remove site" removes the card entirely, so
          this is the only chance to show it. */}
      {deletionReportModal && (
        <ImportReportModal
          reportId={deletionReportModal.reportId}
          sources={deletionReportModal.sources}
          siteName={deletionReportModal.siteName}
          reportType="delete"
          aispaceColor={aispaceColor}
          t={t}
          onClose={() => setDeletionReportModal(null)}
        />
      )}
    </div>
  )
}

export default DefaultFiles
